import React, { useEffect, useMemo, useState } from "react";
import {

  FaShoppingCart,
  FaUsers,
  FaPercentage,
  FaBullhorn,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaFileInvoiceDollar,
  FaStore,
  FaHandHoldingUsd,
  FaExchangeAlt,
  FaReceipt,
  FaMoneyBillWave,
  FaChartPie,
  FaFilter,
  FaDownload,
  FaCalendarAlt,
  FaSearch,
  FaEye,
  FaTable,
  FaChartBar
} from "react-icons/fa";

import Price from "../pageInside/productsPrice"
import OrderShipped from "../pages/orderShipped"
import { elakayAutoText, getElakayLanguageContext, elakayIntlLocale } from "./dataElakay";
import { roleDisplayLabel } from "../../utils/roleDisplayLabel";
import { orderStatusLabel } from "../pages/purchased/OrderStatusBadge";
import { toImageUrl, getElakayImageLoadUrl } from "../../config";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["associateincomedashboard"]?.[key] || fallback;

// ============================================
// STYLES
// ============================================
export const styles = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

/* ===== BASE STYLES ===== */

.associate-income-dashboard {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1e293b;
  padding: 0;
}

.aid-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
}

/* ===== HEADER ===== */
.aid-header {
  margin-bottom: 32px;
}

.aid-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.aid-header-title h1 {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
}

.aid-header-title p {
  color: #64748b;
  font-size: 15px;
  margin: 0;
}

.aid-header-actions {
  display: flex;
  gap: 12px;
}

.aid-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: white;
  color: #475569;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.aid-action-btn:hover {
  background: #f8fafc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.aid-action-btn svg {
  font-size: 16px;
}

/* ===== STATS CARDS ===== */
.aid-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

@media (max-width: 1200px) {
  .aid-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .aid-stats-grid {
    grid-template-columns: 1fr;
  }
}

.aid-stat-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.aid-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.aid-stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.aid-stat-card.sales::before {
  background: linear-gradient(90deg, #007399, #00a4d8);
}

.aid-stat-card.commission::before {
  background: linear-gradient(90deg, #10b981, #059669);
}

.aid-stat-card.fees::before {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.aid-stat-card.profit::before {
  background: linear-gradient(90deg, #06b6d4, #0891b2);
}

.aid-stat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.aid-stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.aid-stat-card.sales .aid-stat-icon {
  background: rgba(0, 115, 153, 0.1);
  color: #007399;
}

.aid-stat-card.commission .aid-stat-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.aid-stat-card.fees .aid-stat-icon {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.aid-stat-card.profit .aid-stat-icon {
  background: rgba(6, 182, 212, 0.1);
  color: #06b6d4;
}

.aid-stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
}

.aid-stat-trend.up {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.aid-stat-trend.down {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.aid-stat-trend.neutral {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}

.aid-stat-value {
  font-family: 'Space Mono', monospace;
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
  letter-spacing: -1px;
}

.aid-stat-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

/* ===== NAVIGATION TABS ===== */
.aid-nav-wrapper {
  background: white;
  border-radius: 16px;
  padding: 8px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.aid-nav-tabs {
  display: flex;
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-x: auto;
}

.aid-nav-tabs::-webkit-scrollbar {
  height: 0;
}

.aid-nav-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 2px solid transparent;
}

.aid-nav-tab:hover {
  background: #f8fafc;
  color: #475569;
}

.aid-nav-tab.active {
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 115, 153, 0.3);
}

.aid-nav-tab svg {
  font-size: 18px;
}

.aid-nav-tab-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.aid-nav-tab.active .aid-nav-tab-badge {
  background: rgba(255, 255, 255, 0.25);
}

/* ===== VIEW SWITCH ===== */
.aid-view-switch-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 18px;
}

.aid-view-switch {
  display: inline-flex;
  gap: 6px;
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.aid-view-switch button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 118px;
  padding: 10px 16px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.aid-view-switch button:hover {
  color: #004b63;
  background: #eef9fc;
}

.aid-view-switch button.active {
  color: white;
  background: linear-gradient(135deg, #007399, #00a4d8);
  box-shadow: 0 4px 12px rgba(0, 115, 153, 0.25);
}

/* ===== ORDER ANALYTICS CHART ===== */
.aid-chart-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.aid-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 22px;
}

.aid-chart-title {
  display: flex;
  gap: 14px;
  align-items: center;
}

.aid-chart-title-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: linear-gradient(135deg, #004b63, #00a4d8);
  font-size: 20px;
  flex-shrink: 0;
}

.aid-chart-title h2 {
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 20px;
  font-weight: 750;
}

.aid-chart-title p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.aid-chart-summary {
  text-align: right;
  min-width: 150px;
}

.aid-chart-summary strong {
  display: block;
  color: #0f172a;
  font: 700 22px 'Space Mono', monospace;
}

.aid-chart-summary span {
  color: #64748b;
  font-size: 12px;
}

.aid-chart-controls {
  display: grid;
  grid-template-columns: minmax(190px, 1fr) minmax(190px, 1fr) auto;
  gap: 14px;
  align-items: end;
  padding: 18px;
  margin-bottom: 18px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.aid-chart-field label {
  display: block;
  margin-bottom: 7px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.aid-chart-field select,
.aid-chart-field input {
  width: 100%;
  min-height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 9px 12px;
  background: white;
  color: #0f172a;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  outline: none;
}

.aid-chart-field select:focus,
.aid-chart-field input:focus {
  border-color: #007399;
  box-shadow: 0 0 0 3px rgba(0, 115, 153, 0.13);
}

.aid-chart-periods {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.aid-chart-period {
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: white;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.aid-chart-period.active {
  color: white;
  border-color: #007399;
  background: linear-gradient(135deg, #007399, #00a4d8);
  box-shadow: 0 4px 12px rgba(0, 115, 153, 0.24);
}

.aid-chart-types {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding-top: 2px;
}

.aid-chart-type {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 8px 13px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
}

.aid-chart-type.active {
  color: #fff;
  border-color: #007399;
  background: #007399;
  box-shadow: 0 4px 12px rgba(0, 115, 153, .2);
}

.aid-chart-custom-dates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  grid-column: 1 / -1;
}

.aid-chart-error {
  grid-column: 1 / -1;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
}

.aid-chart-viewport {
  min-height: 330px;
  overflow-x: auto;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff, #fafbff);
}

.aid-chart-svg {
  display: block;
  width: 100%;
  min-width: 720px;
  height: 330px;
}

.aid-chart-empty {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  text-align: center;
}

.aid-chart-empty svg {
  font-size: 34px;
  margin-bottom: 10px;
}

.aid-chart-empty p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

@media (max-width: 768px) {
  .aid-nav-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  
  .aid-nav-tab {
    justify-content: center;
    padding: 12px 16px;
  }

  .aid-view-switch-wrapper {
    justify-content: stretch;
  }

  .aid-view-switch {
    width: 100%;
  }

  .aid-view-switch button {
    flex: 1;
    min-width: 0;
  }

  .aid-chart-card {
    padding: 18px 14px;
  }

  .aid-chart-header {
    flex-direction: column;
  }

  .aid-chart-summary {
    text-align: left;
  }

  .aid-chart-controls {
    grid-template-columns: 1fr;
    padding: 14px;
  }

  .aid-chart-periods {
    justify-content: flex-start;
  }

  .aid-chart-custom-dates {
    grid-template-columns: 1fr;
  }
}

/* ===== TABLE CARD ===== */
.aid-table-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.aid-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  flex-wrap: wrap;
  gap: 16px;
}

.aid-table-title {
  display: flex;
  align-items: center;
  gap: 14px;
}

.aid-table-title-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.aid-table-title-icon.brand {
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  color: white;
}

.aid-table-title-icon.green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.aid-table-title-icon.orange {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.aid-table-title-icon.blue {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
}

.aid-table-title h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.aid-table-title p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.aid-table-actions {
  display: flex;
  gap: 10px;
}

.aid-table-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
}

.aid-table-search input {
  border: none;
  outline: none;
  font-size: 14px;
  width: 150px;
  font-family: inherit;
}

.aid-table-search input::placeholder {
  color: #94a3b8;
}

/* ===== DATA TABLE ===== */
.aid-data-table-wrapper {
  overflow-x: auto;
  padding: 0;
}

.aid-data-table {
  // width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.aid-data-table thead {
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.aid-data-table thead th {
  // padding: 16px 20px;
  text-align: left;
  // font-weight: 600;
  color: #475569;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.aid-data-table thead th.center {
  text-align: center;
}

.aid-data-table thead th.right {
  text-align: right;
}

.aid-data-table tbody tr {
  transition: background 0.2s;
}

.aid-data-table tbody tr:hover {
  background: #f8fafc;
}

.aid-data-table tbody td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}

.aid-data-table tbody td.center {
  text-align: center;
}

.aid-data-table tbody td.right {
  text-align: right;
}

.aid-data-table tbody tr:last-child td {
  border-bottom: none;
}

/* Row number */
.aid-row-num {
  width: 40px;
  height: 40px;
  background: #f1f5f9;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #64748b;
  font-size: 13px;
}

/* Product cell */
.aid-product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 280px;
}

.aid-product-thumb {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #e0f4fa 0%, #b8e8f5 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007399;
  font-size: 16px;
  flex-shrink: 0;
}

.aid-product-info {
  min-width: 0;
}

.aid-product-name {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.aid-product-id {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

/* Amount cells */
.aid-amount {
  font-family: 'Space Mono', monospace;
  font-weight: 600;
  white-space: nowrap;
}

.aid-amount.positive {
  color: #10b981;
}

.aid-amount.negative {
  color: #ef4444;
}

.aid-amount.neutral {
  color: #0f172a;
}

.aid-amount.highlight {
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Price display */
.aid-price-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.aid-price-current {
  font-family: 'Space Mono', monospace;
  font-weight: 600;
  color: #0f172a;
}

.aid-price-original {
  font-size: 12px;
  color: #94a3b8;
  text-decoration: line-through;
}

/* Status badge */
.aid-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.aid-status-badge.profit {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.aid-status-badge.loss {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.aid-status-badge.even {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}

/* Total row */
.aid-data-table tbody tr.total-row {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.aid-data-table tbody tr.total-row td {
  font-weight: 700;
  color: #0f172a;
  border-top: 2px solid #e2e8f0;
  padding: 20px;
}

.aid-total-label {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

/* ===== EMPTY STATE ===== */
.aid-empty-state {
  text-align: center;
  padding: 80px 24px;
}

.aid-empty-icon {
  width: 80px;
  height: 80px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: #94a3b8;
}

.aid-empty-state h4 {
  font-size: 18px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 8px 0;
}

.aid-empty-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* ===== COMMISSION INFO BANNER ===== */
.aid-info-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: linear-gradient(135deg, rgba(0, 115, 153, 0.08) 0%, rgba(0, 164, 216, 0.05) 100%);
  border: 1px solid rgba(0, 115, 153, 0.15);
  border-radius: 12px;
  margin-bottom: 20px;
}

.aid-info-banner-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.aid-info-banner-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.aid-info-banner-content p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.aid-info-banner-stats {
  display: flex;
  gap: 24px;
  margin-left: auto;
}

.aid-info-stat {
  text-align: center;
}

.aid-info-stat-value {
  font-family: 'Space Mono', monospace;
  font-size: 18px;
  font-weight: 700;
  color: #007399;
}

.aid-info-stat-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .aid-info-banner {
    flex-direction: column;
    text-align: center;
  }
  
  .aid-info-banner-stats {
    margin-left: 0;
    margin-top: 16px;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .aid-container {
    padding: 20px 16px;
  }
  
  .aid-header-top {
    flex-direction: column;
  }
  
  .aid-header-actions {
    width: 100%;
  }
  
  .aid-action-btn {
    flex: 1;
    justify-content: center;
  }
  
  .aid-table-header {
    padding: 20px;
  }
  
  .aid-table-actions {
    width: 100%;
  }
  
  .aid-table-search {
    flex: 1;
  }
  
  .aid-table-search input {
    width: 100%;
  }
  
  .aid-data-table thead th,
  .aid-data-table tbody td {
    padding: 12px 14px;
  }
  
  .aid-product-cell {
    max-width: 160px;
  }
  
  .aid-product-name {
    max-width: 120px;
  }
}
`;

// ============================================
// HELPER COMPONENTS
// ============================================

/**
 * Stats Card Component
 */
const StatCard = ({ type, icon, value, label, trend, trendValue }) => (
  <div className={`aid-stat-card ${type}`}>
    <div className="aid-stat-header">
      <div className="aid-stat-icon">{icon}</div>
      {trend && (
        <div className={`aid-stat-trend ${trend}`}>
          {trend === 'up' && <FaArrowUp size={10} />}
          {trend === 'down' && <FaArrowDown size={10} />}
          {trend === 'neutral' && <FaMinus size={10} />}
          {trendValue}
        </div>
      )}
    </div>
    <div className="aid-stat-value">{value}</div>
    <div className="aid-stat-label">{label}</div>
  </div>
);

/**
 * Format Currency Helper
 */
const formatCurrency = (value) => {
  const amount = Number(value);
  return (Number.isFinite(amount) ? amount : 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
};

const exportRowsToCSV = (filename, rows = []) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const str = value === null || value === undefined ? '' : String(value);
    return `"${str.replace(/"/g, '""')}"`;
  };
  const csv = [
    headers.join(','),
    ...rows.map((row) => headers.map((key) => escape(row[key])).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const matchesSearch = (order, term) =>
  !term || (order?.prod_name || '').toLowerCase().includes(term.toLowerCase());

const hasStoredIncome = (orders = []) =>
  orders.some((order) =>
    Object.prototype.hasOwnProperty.call(order || {}, "incomeAmount")
  );

const getStoredIncomeTotal = (orders = []) =>
  orders.reduce((total, order) => {
    const amount = Number(order?.incomeAmount);
    return total + (Number.isFinite(amount) ? amount : 0);
  }, 0);

const resolveIncomeTotal = (orders = [], fallback = 0) =>
  hasStoredIncome(orders) ? getStoredIncomeTotal(orders) : fallback;

/**
 * Empty State Component
 */
const EmptyState = ({ icon, title, description }) => (
  <div className="aid-empty-state">
    <div className="aid-empty-icon">{icon}</div>
    <h4>{title}</h4>
    <p>{description}</p>
  </div>
);

const toDateInputValue = (date) => {
  const localDate = new Date(
    date.getTime() - (date.getTimezoneOffset() * 60 * 1000)
  );
  return localDate.toISOString().slice(0, 10);
};

const startOfDay = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const addDays = (date, amount) => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

const addMonths = (date, amount) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + amount);
  return result;
};

const safeAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const safeCalculate = (calculation) => {
  try {
    return safeAmount(calculation?.());
  } catch (error) {
    return 0;
  }
};

const getAnalyticsDate = (item) => {
  const value =
    item?.createdAt ||
    item?.products?.createdAt ||
    item?.updatedAt ||
    item?.products?.updatedAt;
  const date = value ? new Date(value) : null;

  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getPeriodBounds = (period, customStart, customEnd) => {
  const now = new Date();
  const today = startOfDay(now);

  if (period === 'day') {
    return { start: today, end: addDays(today, 1), bucket: 'hour' };
  }

  if (period === 'week') {
    return { start: addDays(today, -6), end: addDays(today, 1), bucket: 'day' };
  }

  if (period === 'year') {
    return {
      start: new Date(now.getFullYear(), 0, 1),
      end: new Date(now.getFullYear() + 1, 0, 1),
      bucket: 'month'
    };
  }

  if (period === 'custom') {
    const start = customStart ? startOfDay(new Date(`${customStart}T00:00:00`)) : null;
    const selectedEnd = customEnd ? startOfDay(new Date(`${customEnd}T00:00:00`)) : null;

    if (
      !start ||
      !selectedEnd ||
      Number.isNaN(start.getTime()) ||
      Number.isNaN(selectedEnd.getTime()) ||
      selectedEnd < start
    ) {
      return null;
    }

    const end = addDays(selectedEnd, 1);
    const dayCount = Math.max(1, Math.ceil((end - start) / 86400000));
    const bucket = dayCount > 180 ? 'month' : dayCount > 31 ? 'week' : 'day';
    return { start, end, bucket };
  }

  return { start: addDays(today, -29), end: addDays(today, 1), bucket: 'day' };
};

const createAnalyticsBuckets = (bounds, locale) => {
  if (!bounds) return [];

  const formatter = (options) => {
    try {
      return new Intl.DateTimeFormat(locale || 'en', options);
    } catch (error) {
      return new Intl.DateTimeFormat('en', options);
    }
  };

  const buckets = [];
  let cursor = new Date(bounds.start);

  while (cursor < bounds.end) {
    let next;
    let label;

    if (bounds.bucket === 'hour') {
      next = new Date(cursor);
      next.setHours(next.getHours() + 1);
      label = formatter({ hour: 'numeric' }).format(cursor);
    } else if (bounds.bucket === 'week') {
      next = addDays(cursor, 7);
      label = formatter({ month: 'short', day: 'numeric' }).format(cursor);
    } else if (bounds.bucket === 'month') {
      next = addMonths(cursor, 1);
      label = formatter({ month: 'short' }).format(cursor);
    } else {
      next = addDays(cursor, 1);
      label = formatter({ month: 'short', day: 'numeric' }).format(cursor);
    }

    if (next > bounds.end) next = new Date(bounds.end);
    buckets.push({ start: new Date(cursor), end: next, label, value: 0 });
    cursor = next;
  }

  return buckets;
};

const OrderAnalyticsChart = ({
  infoSource,
  my_Order_as_Seller,
  my_Seller_Order,
  my_Commision_Orders,
  my_Promo_Orders,
  getTotal,
  inComeSeller,
  prodFrTransaction,
  elakayFrais,
  elakayFraisJSON,
  totalFrais,
  calProfit,
  onDateRangeChange
}) => {
  const text =
    infoSource?.laguangeElakay?.(
      'frontElakayHardcodedText',
      infoSource.languageAPI
    )?.associateincomedashboard || {};
  // Intl locale of the active Elakay language (languageAPI is the translation catalogue, not a locale).
  const locale = elakayIntlLocale();
  const today = new Date();
  const defaultStart = addDays(today, -29);
  const [source, setSource] = useState('MyOrdersAsSeller');
  const [metric, setMetric] = useState('productSales');
  const [period, setPeriod] = useState('month');
  const [chartType, setChartType] = useState('combined');
  const [customStart, setCustomStart] = useState(() => toDateInputValue(defaultStart));
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(today));

  const sources = useMemo(() => ([
    {
      key: 'MyOrdersAsSeller',
      label: text.t059 || 'My orders as seller',
      rows: my_Order_as_Seller || [],
      type: 'order'
    },
    {
      key: 'MySellerOrders',
      label: text.t060 || 'My seller orders',
      rows: my_Seller_Order || [],
      type: 'order'
    },
    {
      key: 'MyPromoOrders',
      label: text.t061 || 'My promotion orders',
      rows: my_Promo_Orders?.detail || [],
      type: 'promo'
    },
    {
      key: 'MyOrderIncome',
      label: text.t062 || 'My income from orders',
      rows: my_Commision_Orders?.detail || [],
      type: 'commission'
    }
  ]), [
    my_Order_as_Seller,
    my_Seller_Order,
    my_Promo_Orders,
    my_Commision_Orders,
    text.t059,
    text.t060,
    text.t061,
    text.t062
  ]);

  const selectedSource = sources.find((item) => item.key === source) || sources[0];

  const metrics = useMemo(() => {
    if (selectedSource.type === 'promo') {
      return [
        {
          key: 'productSales',
          label: text.t048 || 'Product sales',
          value: (item) => safeAmount(item?.amountTotal)
        },
        {
          key: 'commission',
          label: text.t055 || 'Commission',
          value: (item) => safeAmount(item?.promoRate)
        }
      ];
    }

    if (selectedSource.type === 'commission') {
      return [
        {
          key: 'productSales',
          label: text.t048 || 'Product sales',
          value: (item) => safeAmount(item?.amountTotal)
        },
        {
          key: 'commission',
          label: text.t055 || 'Commission',
          value: (item) => safeAmount(item?.baseRate)
        }
      ];
    }

    return [
      {
        key: 'productSales',
        label: text.t048 || 'Product sales',
        value: (item) => safeCalculate(() => getTotal?.([item]))
      },
      {
        key: 'sellerIncome',
        label: text.t049 || 'Seller income',
        value: (item) => resolveIncomeTotal(
          [item],
          safeCalculate(() => inComeSeller?.([item]))
        )
      },
      {
        key: 'transactionFees',
        label: text.t050 || 'Transaction fees',
        value: (item) => safeCalculate(() => prodFrTransaction?.([item]))
      },
      {
        key: 'fees',
        label: text.t051 || 'Fees',
        value: (item) => safeCalculate(() => elakayFrais?.([item]))
      },
      {
        key: 'totalFees',
        label: text.t052 || 'Total fees',
        value: (item) => safeCalculate(() => totalFrais?.([item]))
      },
      {
        key: 'profit',
        label: text.t053 || 'Profit',
        value: (item) => Math.max(
          0,
          safeCalculate(
            () => calProfit?.([item], 0, item?.prod_costPrice)?.profit
          )
        )
      },
      {
        key: 'loss',
        label: text.t054 || 'Loss',
        value: (item) => safeCalculate(
          () => calProfit?.([item], 0, item?.prod_costPrice)?.loss
        )
      },
      {
        key: 'commission',
        label: text.t055 || 'Commission',
        value: (item) => resolveIncomeTotal(
          [item],
          safeCalculate(() => elakayFraisJSON?.([item])?.payAddONSeller)
        )
      }
    ];
  }, [
    selectedSource.type,
    text.t048,
    text.t049,
    text.t050,
    text.t051,
    text.t052,
    text.t053,
    text.t054,
    text.t055,
    getTotal,
    inComeSeller,
    prodFrTransaction,
    elakayFrais,
    totalFrais,
    calProfit,
    elakayFraisJSON
  ]);

  useEffect(() => {
    if (!metrics.some((item) => item.key === metric)) {
      setMetric(metrics[0]?.key || 'productSales');
    }
  }, [metric, metrics]);

  useEffect(() => {
    if (!onDateRangeChange) return;

    const apiRanges = {
      day: { orderDate: 'days', myd: 1, startDate: '', endDate: '' },
      week: { orderDate: 'days', myd: 7, startDate: '', endDate: '' },
      month: { orderDate: 'months', myd: 1, startDate: '', endDate: '' },
      year: {
        orderDate: 'years',
        myd: new Date().getFullYear(),
        startDate: '',
        endDate: ''
      }
    };

    if (period === 'custom') {
      const bounds = getPeriodBounds(period, customStart, customEnd);
      if (!bounds) return;
      onDateRangeChange({
        orderDate: 'custom',
        myd: '',
        startDate: customStart,
        endDate: customEnd
      });
      return;
    }

    onDateRangeChange(apiRanges[period] || apiRanges.month);
  }, [period, customStart, customEnd, onDateRangeChange]);

  const bounds = useMemo(
    () => getPeriodBounds(period, customStart, customEnd),
    [period, customStart, customEnd]
  );
  const selectedMetric = metrics.find((item) => item.key === metric) || metrics[0];

  const chartData = useMemo(() => {
    const buckets = createAnalyticsBuckets(bounds, locale);
    let recordCount = 0;

    selectedSource.rows.forEach((item) => {
      const date = getAnalyticsDate(item);
      if (!date || !bounds || date < bounds.start || date >= bounds.end) return;

      const bucket = buckets.find(
        (candidate) => date >= candidate.start && date < candidate.end
      );
      if (!bucket) return;

      bucket.value += safeAmount(selectedMetric?.value(item));
      recordCount += 1;
    });

    return { buckets, recordCount };
  }, [bounds, locale, selectedMetric, selectedSource.rows]);

  const total = chartData.buckets.reduce((sum, item) => sum + item.value, 0);
  const maxValue = Math.max(...chartData.buckets.map((item) => item.value), 0);
  const minValue = Math.min(...chartData.buckets.map((item) => item.value), 0);
  const chartMax = Math.max(0, maxValue);
  const chartMin = Math.min(0, minValue);
  const valueRange = chartMax - chartMin || 1;
  const width = 1000;
  const height = 330;
  const margin = { top: 24, right: 24, bottom: 58, left: 82 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const step = plotWidth / Math.max(chartData.buckets.length, 1);
  const barWidth = Math.max(5, Math.min(28, step * 0.58));
  const labelEvery = Math.max(1, Math.ceil(chartData.buckets.length / 8));
  const valueToY = (value) => (
    chartMax === chartMin
      ? margin.top + plotHeight
      : margin.top + ((chartMax - value) / valueRange) * plotHeight
  );
  const zeroY = valueToY(0);
  const points = chartData.buckets.map((item, index) => {
    const x = margin.left + (step * index) + (step / 2);
    const y = valueToY(item.value);
    return { ...item, x, y };
  });
  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
  const areaPath = points.length
    ? `${linePath} L ${points[points.length - 1].x} ${zeroY} L ${points[0].x} ${zeroY} Z`
    : '';
  const periods = [
    { key: 'day', label: text.t041 || 'Day' },
    { key: 'week', label: text.t042 || 'Week' },
    { key: 'month', label: text.t043 || 'Month' },
    { key: 'year', label: text.t044 || 'Year' },
    { key: 'custom', label: text.t045 || 'Custom' }
  ];

  return (
    <section className="aid-chart-card">
      <div className="aid-chart-header">
        <div className="aid-chart-title">
          <div className="aid-chart-title-icon"><FaChartLine /></div>
          <div>
            <h2>{text.t037 || elakayAutoText("h_ce5988abf83a12", "Order performance")}</h2>
            <p>{text.t038 || elakayAutoText("h_738e5c0a0513bb", "Select a data source, metric, and reporting period.")}</p>
          </div>
        </div>
        <div className="aid-chart-summary">
          <strong>{formatCurrency(total)}</strong>
          <span>
            {text.t057 || elakayAutoText("h_4ad76f39d40100", "Total for period")} · {chartData.recordCount} {text.t058 || elakayAutoText("h_9658403816409e", "orders")}
          </span>
        </div>
      </div>

      <div className="aid-chart-controls">
        <div className="aid-chart-types" role="group" aria-label={elakayAutoText("h_879575b290bb4f", "Graph type")}>
          {[
            { key: 'line', label: elakayAutoText("h_ea967600376281", "Line"), icon: <FaChartLine /> },
            { key: 'area', label: elakayAutoText("h_income_area_chart", "Area"), icon: <FaChartPie /> },
            { key: 'bar', label: elakayAutoText("h_e496fd20136d4b", "Bar"), icon: <FaChartBar /> },
            { key: 'combined', label: elakayAutoText("h_b6c533b56588b0", "Combined"), icon: <FaExchangeAlt /> }
          ].map((type) => (
            <button
              type="button"
              key={type.key}
              className={`aid-chart-type ${chartType === type.key ? 'active' : ''}`}
              aria-pressed={chartType === type.key}
              onClick={() => setChartType(type.key)}
            >
              {type.icon}{type.label}
            </button>
          ))}
        </div>
        <div className="aid-chart-field">
          <label htmlFor="aid-chart-source">{text.t039 || elakayAutoText("h_ffb77dd8c3e5f6", "Data source")}</label>
          <select
            id="aid-chart-source"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            {sources.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        </div>

        <div className="aid-chart-field">
          <label htmlFor="aid-chart-metric">{text.t040 || elakayAutoText("h_b2bb7604c825f9", "Metric")}</label>
          <select
            id="aid-chart-metric"
            value={selectedMetric?.key || ''}
            onChange={(event) => setMetric(event.target.value)}
          >
            {metrics.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        </div>

        <div className="aid-chart-periods" aria-label={text.t031 || elakayAutoText("h_c65ea4ae013589", "Date range")}>
          {periods.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`aid-chart-period ${period === item.key ? 'active' : ''}`}
              onClick={() => setPeriod(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="aid-chart-custom-dates">
            <div className="aid-chart-field">
              <label htmlFor="aid-chart-start">{text.t046 || elakayAutoText("h_ff99f5b5f7948d", "Start date")}</label>
              <input
                id="aid-chart-start"
                type="date"
                value={customStart}
                max={customEnd}
                onChange={(event) => setCustomStart(event.target.value)}
              />
            </div>
            <div className="aid-chart-field">
              <label htmlFor="aid-chart-end">{text.t047 || elakayAutoText("h_89d10cd6c1e143", "End date")}</label>
              <input
                id="aid-chart-end"
                type="date"
                value={customEnd}
                min={customStart}
                max={toDateInputValue(new Date())}
                onChange={(event) => setCustomEnd(event.target.value)}
              />
            </div>
          </div>
        )}

        {!bounds && (
          <div className="aid-chart-error">
            {text.t064 || elakayAutoText("h_49606d213a26ef", "The selected date range is invalid.")}
          </div>
        )}
      </div>

      <div className="aid-chart-viewport">
        {chartData.recordCount === 0 || !bounds ? (
          <div className="aid-chart-empty">
            <FaChartLine />
            <p>{text.t056 || elakayAutoText("h_ec51e1e08eba8a", "No data is available for this period.")}</p>
          </div>
        ) : (
          <svg
            className="aid-chart-svg"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`${selectedSource.label}: ${selectedMetric?.label}`}
          >
            <defs>
              <linearGradient id="aid-chart-bars" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#007399" />
                <stop offset="100%" stopColor="#00a4d8" />
              </linearGradient>
            </defs>

            {[0, 1, 2, 3, 4].map((tick) => {
              const ratio = tick / 4;
              const y = margin.top + (plotHeight * ratio);
              const value = chartMax === chartMin
                ? 0
                : chartMax - (valueRange * ratio);
              return (
                <g key={tick}>
                  <line
                    x1={margin.left}
                    x2={width - margin.right}
                    y1={y}
                    y2={y}
                    stroke="#e2e8f0"
                    strokeDasharray="4 6"
                  />
                  <text
                    x={margin.left - 12}
                    y={y + 4}
                    textAnchor="end"
                    fill="#64748b"
                    fontSize="11"
                  >
                    {formatCurrency(value)}
                  </text>
                </g>
              );
            })}

            {(chartType === 'bar' || chartType === 'combined') && points.map((point, index) => {
              const barHeight = Math.abs(zeroY - point.y);
              return (
                <g key={`${point.start.toISOString()}-${index}`}>
                  <rect
                    x={point.x - (barWidth / 2)}
                    y={Math.min(point.y, zeroY)}
                    width={barWidth}
                    height={barHeight}
                    rx="5"
                    fill="url(#aid-chart-bars)"
                    opacity="0.82"
                  >
                    <title>{`${point.label}: ${formatCurrency(point.value)}`}</title>
                  </rect>
                </g>
              );
            })}

            {chartType === 'area' && (
              <path d={areaPath} fill="url(#aid-chart-bars)" opacity="0.22" />
            )}
            {(chartType === 'line' || chartType === 'area' || chartType === 'combined') && <path
                d={linePath}
                fill="none"
                stroke="#004b63"
                strokeWidth="3"
                strokeLinejoin="round"
                strokeLinecap="round"
              />}
            {(chartType === 'line' || chartType === 'area' || chartType === 'combined') && points.map((point, index) => (
              <circle
                key={`point-${index}`}
                cx={point.x}
                cy={point.y}
                r="3.5"
                fill="#ffffff"
                stroke="#004b63"
                strokeWidth="2"
              >
                <title>{`${point.label}: ${formatCurrency(point.value)}`}</title>
              </circle>
            ))}
            {points.map((point, index) => (
              (index % labelEvery === 0 || index === points.length - 1) && (
                <text key={`label-${index}`} x={point.x} y={height - 24} textAnchor="middle" fill="#64748b" fontSize="11">
                  {point.label}
                </text>
              )
            ))}
          </svg>
        )}
      </div>
    </section>
  );
};

// ============================================
// TABLE COMPONENTS
// ============================================

/**
 * My Orders As Seller Table
 */
const MyOrdersAsSellerTable = ({
  orders = [],
  infoSource,
  getTotal,
  inComeSeller,
  prodFrTransaction,
  elakayFrais,
  totalFrais,
  calProfit,
  ItemsOrder
}) => {
  // __setElakayInfoSource(infoSource);
  const lang = (page, key) => infoSource?.laguangeElakay?.(page, infoSource.languageAPI)?.[key] || key;
  const [searchTerm, setSearchTerm] = useState("");
  const filteredOrders = useMemo(
    () => orders.filter((order) => matchesSearch(order, searchTerm)),
    [orders, searchTerm]
  );

  // Calculate totals — memoized so these full-array recomputations only
  // run when filteredOrders (or the calc functions) actually change, not
  // on every render of this dashboard.
  const totalSales = useMemo(() => getTotal?.(filteredOrders) || 0, [filteredOrders, getTotal]);
  const totalIncome = useMemo(
    () => resolveIncomeTotal(filteredOrders, inComeSeller?.(filteredOrders) || 0),
    [filteredOrders, inComeSeller]
  );
  const totalTransFees = useMemo(() => prodFrTransaction?.(filteredOrders) || 0, [filteredOrders, prodFrTransaction]);
  const totalElakayFees = useMemo(() => elakayFrais?.(filteredOrders) || 0, [filteredOrders, elakayFrais]);
  const totalAllFees = useMemo(() => totalFrais?.(filteredOrders) || 0, [filteredOrders, totalFrais]);

  const handleExport = () => {
    const rows = filteredOrders.map((order) => {
      const profitData = calProfit?.([order], 0, order.prod_costPrice) || {};
      return {
        Product: order.prod_name || '',
        Sales: getTotal?.([order]) || 0,
        SellerIncome: resolveIncomeTotal([order], inComeSeller?.([order]) || 0),
        TransactionFees: prodFrTransaction?.([order]) || 0,
        Fees: elakayFrais?.([order]) || 0,
        TotalFees: totalFrais?.([order]) || 0,
        Profit: profitData.profit || 0,
        Loss: profitData.loss || 0,
        Status: profitData.statut || ''
      };
    });
    exportRowsToCSV('my-sales-orders.csv', rows);
  };

  return (
    <div className="aid-table-card">
      <div className="aid-table-header">
        <div className="aid-table-title">
          <div className="aid-table-title-icon brand">
            <FaStore />
          </div>
          <div>
            <h3>{lang('walletPage', 'viewincommeSell') || elakayAutoText("h_d592cb32d6bc14", "My Sales Orders")}</h3>
            <p>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t001}</p>
          </div>
        </div>
        <div className="aid-table-actions">
          <div className="aid-table-search">
            <FaSearch />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t002}
            />
          </div>
          <button className="aid-action-btn" onClick={handleExport}>
            <FaDownload />{__elakayText("t035", "Export")}</button>
        </div>
      </div>

      <div className="aid-data-table-wrapper">
        {filteredOrders.length === 0 ? (
          <EmptyState
            icon={<FaShoppingCart />}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t003}
            description={elakayAutoText("h_6fe2b86b7c6274", "Your seller orders will appear here")}
          />
        ) : (
          <table className="aid-data-table">
            <thead>
              <tr>
               <th  className="center">#</th>
                <th className="center" >{lang('walletPage', 'name')}</th>
                <th className="center">{lang('walletPage', 'productPrice')}</th>
                <th className="center">{lang('sellPage', 'productSale')}</th>
                <th className="center">{lang('walletPage', 'incomeSeller')}</th>
                <th className="center">{lang('sellPage', 'transFeet')}</th>
                <th className="center">{lang('sellPage', 'feet')}</th>
                <th className="center">{lang('sellPage', 'totalFeet')}</th>
                <th className="center">{lang('sellPage', 'profit')}</th>
                <th className="center">{lang('sellPage', 'loss')}</th>
                <th className="center">{lang('sellPage', 'statut')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, i) => {
                const profitData = calProfit?.([order], 0, order.prod_costPrice) || {};
                return (
                  <tr key={order._id || i}>
                    <td>
                      <div className="aid-row-num">{i + 1}</div>
                    </td>
                    <td>
                      <div className="aid-product-cell">
                        <div className="aid-product-thumb">
                          <FaShoppingCart />
                        </div>
                        <div className="aid-product-info">
                          <div 
                            className="aid-product-name" 
                            title={order.prod_name}
                            dangerouslySetInnerHTML={{ __html: order.prod_name?.substring(0, 30) + '...' }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="right">
                      {/* <PriceDisplay 
                        price={order.products?.price} 
                        salePrice={order.products?.salePrice} 
                      /> */}
                         <Price
                        showAddToCartButton={false}
                        products={{
                          price: order.products.price,
                          salePrice: order.products.salePrice
                        }}
                      />
                    </td>
                    <td className="right">
                      <span className="aid-amount neutral">{formatCurrency(getTotal?.([order]))}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount highlight">
                        {formatCurrency(resolveIncomeTotal(
                          [order],
                          inComeSeller?.([order]) || 0
                        ))}
                      </span>
                    </td>
                    <td className="right">
                      <span className="aid-amount negative">{formatCurrency(prodFrTransaction?.([order]))}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount negative">{formatCurrency(elakayFrais?.([order]))}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount negative">{formatCurrency(totalFrais?.([order]))}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount positive">{formatCurrency(profitData.profit)}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount negative">{formatCurrency(profitData.loss)}</span>
                    </td>
                    <td className="center">
                      <span className={`aid-status-badge ${profitData.statut?.toLowerCase() || 'even'}`}>
                        {profitData.statut === 'Profit' && <FaArrowUp size={10} />}
                        {profitData.statut === 'Loss' && <FaArrowDown size={10} />}
                        {profitData.statut || elakayAutoText("h_9e767ad03e5547", "Even")}
                      </span>
                    </td>
                  </tr>
                );
              })}
              
              {/* Total Row */}
              <tr 
              // className="total-row"
              >
                <td colSpan={2} ></td>
        
                <td className="aid-total-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t004}</td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(ItemsOrder?.(filteredOrders))}</span>
                </td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(totalSales)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount highlight">{formatCurrency(totalIncome)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount negative">{formatCurrency(totalTransFees)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount negative">{formatCurrency(totalElakayFees)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount negative">{formatCurrency(totalAllFees)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/**
 * Commission For My Sellers Table
 */
const CommissionForMySellersTable = ({
  orders = [],
  infoSource,
  getTotal,
  elakayFraisJSON,
  ItemsOrder
}) => {
  const lang = (page, key) => infoSource?.laguangeElakay?.(page, infoSource.languageAPI)?.[key] || key;
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredOrders = useMemo(
    () => orders.filter((order) => matchesSearch(order, searchTerm)),
    [orders, searchTerm]
  );
  const totals = useMemo(() => elakayFraisJSON?.(filteredOrders) || {}, [filteredOrders, elakayFraisJSON]);
  const storedCommissionTotal = useMemo(
    () => resolveIncomeTotal(filteredOrders, totals.payAddONSeller || 0),
    [filteredOrders, totals]
  );

  const handleExport = () => {
    const rows = filteredOrders.map((order) => {
      const fees = elakayFraisJSON?.([order]) || {};
      return {
        Product: order.prod_name || '',
        Sales: getTotal?.([order]) || 0,
        Commission: resolveIncomeTotal([order], fees.payAddONSeller || 0),
        Elakay: fees.payElakay || 0,
        Total: fees.totalWithoutFixedFee || 0
      };
    });
    exportRowsToCSV('commission-from-my-sellers.csv', rows);
  };

  return (
    <div className="aid-table-card">
      {/* Info Banner */}
      <div style={{ padding: '24px 28px 0' }}>
        <div className="aid-info-banner">
          <div className="aid-info-banner-icon">
            <FaPercentage />
          </div>
          <div className="aid-info-banner-content">
            <h4>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t005}</h4>
            <p>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t006}</p>
          </div>
          <div className="aid-info-banner-stats">
            <div className="aid-info-stat">
              <div className="aid-info-stat-value">1/3</div>
              <div className="aid-info-stat-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t007}</div>
            </div>
            <div className="aid-info-stat">
              <div className="aid-info-stat-value">2/3</div>
              <div className="aid-info-stat-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t008}</div>
            </div>
            <div className="aid-info-stat">
              <div className="aid-info-stat-value">6%</div>
              <div className="aid-info-stat-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t009}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="aid-table-header" style={{ borderTop: 'none', paddingTop: 16 }}>
        <div className="aid-table-title">
          <div className="aid-table-title-icon green">
            <FaUsers />
          </div>
          <div>
            <h3>{lang('walletPage', 'commissionForMySeller') || elakayAutoText("h_ab40049ef8c7f3", "Commission from My Sellers")}</h3>
            <p>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t010}</p>
          </div>
        </div>
        <div className="aid-table-actions">
          {showFilter && (
            <div className="aid-table-search">
              <FaSearch />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t002}
              />
            </div>
          )}
          <button className="aid-action-btn" onClick={() => setShowFilter((value) => !value)}>
            <FaFilter />{__elakayText("t036", "Filter")}</button>
          <button className="aid-action-btn" onClick={handleExport}>
            <FaDownload />{__elakayText("t035", "Export")}</button>
        </div>
      </div>

      <div className="aid-data-table-wrapper">
        {filteredOrders.length === 0 ? (
          <EmptyState
            icon={<FaUsers />}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t011}
            description={elakayAutoText("h_d308e50214e88d", "Commissions from your sellers will appear here")}
          />
        ) : (
          <table className="aid-data-table">
            <thead>
              <tr>
                <th className="center">#</th>
                <th className="center">{lang('walletPage', 'name')}</th>
                <th className="center">{lang('walletPage', 'productPrice')}</th>
                <th className="center">{lang('sellPage', 'productSale')}</th>
                <th className="center">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t012}</th>
                <th className="center">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t013}</th>
                <th className="center">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t014}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, i) => {
                const fees = elakayFraisJSON?.([order]) || {};
                return (
                  <tr key={order._id || i}>
                    <td>
                      <div className="aid-row-num">{i + 1}</div>
                    </td>
                    <td>
                      <div className="aid-product-cell">
                        <div className="aid-product-thumb">
                          <FaShoppingCart />
                        </div>
                        <div className="aid-product-info">
                          <div className="aid-product-name" title={order.prod_name}>
                            {order.prod_name?.substring(0, 30)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="right">
                       <Price
                        showAddToCartButton={false}
                        products={{
                          price: order.products.price,
                          salePrice: order.products.salePrice
                        }}
                      />
                    </td>
                    <td className="right">
                      <span className="aid-amount neutral">{formatCurrency(getTotal?.([order]))}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount positive">
                        {formatCurrency(resolveIncomeTotal(
                          [order],
                          fees.payAddONSeller || 0
                        ))}
                      </span>
                    </td>
                    <td className="right">
                      <span className="aid-amount neutral">{formatCurrency(fees.payElakay)}</span>
                    </td>
                    <td className="right">
                      <span className="aid-amount highlight">{formatCurrency(fees.totalWithoutFixedFee)}</span>
                    </td>
                  </tr>
                );
              })}
              
              {/* Total Row */}
              <tr 
              // className="total-row"
              >
                <td></td>
                <td className="aid-total-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t015}</td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(ItemsOrder?.(filteredOrders))}</span>
                </td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(getTotal?.(filteredOrders))}</span>
                </td>
                <td className="right">
                  <span className="aid-amount positive">{formatCurrency(storedCommissionTotal)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(totals.payElakay)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount highlight">{formatCurrency(totals.totalWithoutFixedFee)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/**
 * Commission From Orders Table
 */
const CommissionFromOrdersTable = ({
  commissionData = {},
  infoSource,
  onShowChart
}) => {
  const lang = (page, key) => infoSource?.laguangeElakay?.(page, infoSource.languageAPI)?.[key] || key;
  const orders = commissionData.detail || [];

  const handleExport = () => {
    const rows = orders.map((order) => ({
      Product: order.prod_name || '',
      Sales: order.amountTotal || 0,
      Commission: order.baseRate || 0
    }));
    exportRowsToCSV('commission-from-orders.csv', rows);
  };

  return (
    <div className="aid-table-card">
      <div className="aid-table-header">
        <div className="aid-table-title">
          <div className="aid-table-title-icon orange">
            <FaHandHoldingUsd />
          </div>
          <div>
            <h3>{lang('walletPage', 'commissionFromOrders') || elakayAutoText("h_d6b7b072122a5f", "Commission from Orders")}</h3>
            <p>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t016}</p>
          </div>
        </div>
        <div className="aid-table-actions">
          <button className="aid-action-btn" onClick={() => onShowChart?.()}>
            <FaCalendarAlt />{__elakayText("t031", "Date Range")}</button>
          <button className="aid-action-btn" onClick={handleExport}>
            <FaDownload />{__elakayText("t035", "Export")}</button>
        </div>
      </div>

      <div className="aid-data-table-wrapper">
        {orders.length === 0 ? (
          <EmptyState
            icon={<FaFileInvoiceDollar />}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t017}
            description={elakayAutoText("h_c189a197b9b0eb", "Your order-based commissions will appear here")}
          />
        ) : (
          <table className="aid-data-table">
            <thead>
              <tr>
                <th className="center">#</th>
                <th className="center">{lang('walletPage', 'name')}</th>
                <th className="center">{lang('sellPage', 'productSale')}</th>
                <th className="center">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t018}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, i) => (
                <tr key={order._id || i}>
                  <td>
                    <div className="aid-row-num">{i + 1}</div>
                  </td>
                  <td>
                    <div className="aid-product-cell">
                      <div className="aid-product-thumb">
                        <FaReceipt />
                      </div>
                      <div className="aid-product-info">
                        <div className="aid-product-name" title={order.prod_name}>
                          {order.prod_name?.substring(0, 35)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="right">
                    <span className="aid-amount neutral">{formatCurrency(order.amountTotal)}</span>
                  </td>
                  <td className="right">
                    <span className="aid-amount positive">{formatCurrency(order.baseRate)}</span>
                  </td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr 
              // className="total-row"
              >
                <td></td>
                <td className="aid-total-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t019}</td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(commissionData.totalAmountEligible)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount positive">{formatCurrency(commissionData.totalBaseRate)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/**
 * My Promotion Orders Table
 */
export const MyPromotionOrdersTable = ({
  promoData = {},
  infoSource,
  onShowChart
}) => {
  const lang = (page, key) => infoSource?.laguangeElakay?.(page, infoSource.languageAPI)?.[key] || key;
  const orders = promoData.detail || [];

  const handleExport = () => {
    const rows = orders.map((order) => ({
      Product: order.prod_name || '',
      Sales: order.amountTotal || 0,
      PromoCommission: order.promoRate || 0
    }));
    exportRowsToCSV('my-promotion-orders.csv', rows);
  };

  return (
    <div className="aid-table-card">
      <div className="aid-table-header">
        <div className="aid-table-title">
          <div className="aid-table-title-icon blue">
            <FaBullhorn />
          </div>
          <div>
            <h3>{lang('walletPage', 'myPromotionOrders') || elakayAutoText("h_e1f8ed2187831f", "My Promotion Orders")}</h3>
            <p>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t020}</p>
          </div>
        </div>
        <div className="aid-table-actions">
          <button className="aid-action-btn" onClick={() => onShowChart?.()}>
            <FaEye />{__elakayText("t032", "View Campaigns")}</button>
          <button className="aid-action-btn" onClick={handleExport}>
            <FaDownload />{__elakayText("t035", "Export")}</button>
        </div>
      </div>

      <div className="aid-data-table-wrapper">
        {orders.length === 0 ? (
          <EmptyState
            icon={<FaBullhorn />}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t021}
            description={elakayAutoText("h_3808e5748b6373", "Your promotional earnings will appear here")}
          />
        ) : (
          <table className="aid-data-table">
            <thead>
              <tr>
                <th className="center">#</th>
                <th className="center">{lang('walletPage', 'name')}</th>
                <th className="center">{lang('sellPage', 'productSale')}</th>
                <th className="center">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t022}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, i) => (
                <tr key={order._id || i}>
                  <td>
                    <div className="aid-row-num">{i + 1}</div>
                  </td>
                  <td>
                    <div className="aid-product-cell">
                      <div className="aid-product-thumb" style={{ background: 'linear-gradient(135deg, #e0f4fa 0%, #b8e8f5 100%)', color: '#007399' }}>
                        <FaBullhorn />
                      </div>
                      <div className="aid-product-info">
                        <div className="aid-product-name" title={order.prod_name}>
                          {order.prod_name?.substring(0, 35)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="right">
                    <span className="aid-amount neutral">{formatCurrency(order.amountTotal)}</span>
                  </td>
                  <td className="right">
                    <span className="aid-amount positive">{formatCurrency(order.promoRate)}</span>
                  </td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr 
              // className="total-row"
              >
                <td></td>
                <td className="aid-total-label">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t023}</td>
                <td className="right">
                  <span className="aid-amount neutral">{formatCurrency(promoData.totalAmountPromo)}</span>
                </td>
                <td className="right">
                  <span className="aid-amount positive">{formatCurrency(promoData.totalPromoRate)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const paymentIntentDashboardStyles = `
.aid-income-v2 {
  min-height: 420px;
  padding: 28px 24px 44px;
  background: #f6f8fb;
  color: #172033;
}

.aid-income-v2-inner {
  width: min(1380px, 100%);
  margin: 0 auto;
}

.aid-income-v2-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.aid-income-v2-title h1 {
  margin: 0 0 6px;
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 800;
  color: #172033;
  letter-spacing: -0.04em;
}

.aid-income-v2-title p {
  max-width: 660px;
  margin: 0;
  color: #697386;
  font-size: 14px;
}

.aid-income-v2-live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d8e3eb;
  border-radius: 999px;
  background: #fff;
  color: #3b5264;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.aid-income-v2-live::before {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #27ae60;
  content: "";
  box-shadow: 0 0 0 4px rgba(39, 174, 96, 0.12);
}

.aid-income-v2-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.aid-income-v2-stat {
  position: relative;
  overflow: hidden;
  min-height: 128px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 26px rgba(15, 23, 42, 0.045);
}

.aid-income-v2-stat::after {
  position: absolute;
  right: -18px;
  bottom: -26px;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: currentColor;
  content: "";
  opacity: 0.055;
}

.aid-income-v2-stat.total { color: #007399; }
.aid-income-v2-stat.paid { color: #168653; }
.aid-income-v2-stat.pending { color: #c47700; }
.aid-income-v2-stat.orders { color: #5f4bb6; }

.aid-income-v2-category-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: -4px 0 18px;
}

.aid-income-v2-category-card {
  padding: 13px 15px;
  border: 1px solid #e3e9f0;
  border-left: 4px solid var(--aid-income-color, #007399);
  border-radius: 12px;
  background: #fff;
}

.aid-income-v2-category-card span { display: block; color: #7b8799; font-size: 11px; font-weight: 800; }
.aid-income-v2-category-card strong { display: block; margin: 4px 0 3px; color: #172033; font-family: 'Space Mono', monospace; font-size: 17px; }
.aid-income-v2-category-card small { color: #929cad; font-size: 10px; }

.aid-income-v2-stat-icon {
  display: grid;
  width: 36px;
  height: 36px;
  margin-bottom: 14px;
  place-items: center;
  border-radius: 10px;
  background: currentColor;
  color: #fff;
}

.aid-income-v2-stat strong {
  display: block;
  margin-bottom: 3px;
  color: #172033;
  font-family: 'Space Mono', monospace;
  font-size: 22px;
}

.aid-income-v2-stat span {
  color: #778195;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.aid-income-v2-filters {
  display: grid;
  grid-template-columns: repeat(3, minmax(150px, 1fr)) repeat(2, minmax(145px, .8fr)) auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e1e7ef;
  border-radius: 16px;
  background: #fff;
}

.aid-income-v2-filter label {
  display: block;
  margin-bottom: 6px;
  color: #667085;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.aid-income-v2-filter select,
.aid-income-v2-filter input {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #d7dee8;
  border-radius: 10px;
  outline: none;
  background: #fbfcfe;
  color: #263243;
  font-size: 13px;
}

.aid-income-v2-filter select:focus,
.aid-income-v2-filter input:focus {
  border-color: #258ecd;
  box-shadow: 0 0 0 3px rgba(37, 142, 205, .12);
}

.aid-income-v2-clear {
  min-height: 40px;
  padding: 8px 14px;
  border: 1px solid #d7dee8;
  border-radius: 10px;
  background: #fff;
  color: #4b596c;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.aid-income-v2-clear:hover { background: #f4f7fa; }

.aid-income-v2-view-toggle-wrap {
  display: flex;
  justify-content: flex-end;
  margin: -6px 0 18px;
}

.aid-income-v2-view-toggle {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(104px, 1fr));
  gap: 4px;
  padding: 4px;
  border: 1px solid #dce3eb;
  border-radius: 13px;
  background: #edf2f6;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, .04);
}

.aid-income-v2-view-toggle button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 8px 14px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #68758a;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  transition: background .2s ease, color .2s ease, box-shadow .2s ease;
}

.aid-income-v2-view-toggle button.active {
  background: #fff;
  color: #007399;
  box-shadow: 0 2px 8px rgba(15, 23, 42, .12);
}

.aid-income-v2-view-toggle button:focus-visible {
  outline: 3px solid rgba(37, 142, 205, .2);
  outline-offset: 1px;
}

.aid-income-v2-chart {
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #dfe6ee;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .04);
}

.aid-income-v2-chart-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.aid-income-v2-chart-title {
  display: flex;
  align-items: center;
  gap: 11px;
}

.aid-income-v2-chart-title-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 11px;
  background: #eaf5fa;
  color: #007399;
}

.aid-income-v2-chart-title h2 {
  margin: 0 0 3px;
  color: #243044;
  font-size: 16px;
  font-weight: 800;
}

.aid-income-v2-chart-title p {
  margin: 0;
  color: #8791a3;
  font-size: 12px;
}

.aid-income-v2-chart-legend {
  display: flex;
  gap: 14px;
  color: #667085;
  font-size: 11px;
  font-weight: 700;
}

.aid-income-v2-chart-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.aid-income-v2-chart-period {
  display: flex;
  align-items: center;
  gap: 8px;
}

.aid-income-v2-chart-period label {
  color: #748096;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.aid-income-v2-chart-period select {
  min-width: 148px;
  min-height: 36px;
  padding: 7px 30px 7px 10px;
  border: 1px solid #d7dee8;
  border-radius: 9px;
  outline: none;
  background: #fbfcfe;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.aid-income-v2-chart-period select:focus {
  border-color: #258ecd;
  box-shadow: 0 0 0 3px rgba(37, 142, 205, .12);
}

.aid-income-v2-chart-period select:disabled {
  color: #007399;
  cursor: not-allowed;
  opacity: 1;
}

.aid-income-v2-chart-types {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.aid-income-v2-chart-types button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 32px;
  padding: 6px 9px;
  border: 1px solid #d7dee8;
  border-radius: 8px;
  background: #fff;
  color: #68758a;
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
}

.aid-income-v2-chart-types button.active {
  border-color: #007399;
  background: #eaf5fa;
  color: #007399;
}

.aid-income-v2-chart-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.aid-income-v2-chart-dot {
  width: 9px;
  height: 9px;
  border-radius: 3px;
}

.aid-income-v2-chart-dot.paid { background: #168653; }
.aid-income-v2-chart-dot.pending { background: #e1a23a; }

.aid-income-v2-chart-scroll {
  overflow-x: auto;
  padding: 8px 4px 2px;
}

.aid-income-v2-chart-visual {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  align-items: stretch;
}

.aid-income-v2-chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 260px;
  padding: 17px 10px 43px 0;
  border-right: 1px solid #dfe5ec;
}

.aid-income-v2-chart-y-axis span {
  color: #748096;
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  text-align: right;
  white-space: nowrap;
}

.aid-income-v2-chart-plot {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--aid-chart-columns), minmax(54px, 1fr));
  gap: 12px;
  align-items: end;
  min-width: max(100%, calc(var(--aid-chart-columns) * 68px));
  height: 260px;
  padding: 18px 8px 0;
  border-bottom: 1px solid #dfe5ec;
  background: repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent 59px,
    #edf1f5 60px
  );
}

.aid-income-v2-chart-line-overlay {
  position: absolute;
  inset: 18px 8px 34px;
  width: calc(100% - 16px);
  height: calc(100% - 52px);
  overflow: visible;
  pointer-events: none;
}

.aid-income-v2-line-stage {
  position: relative;
  min-width: max(100%, calc(var(--aid-chart-columns) * 68px));
  height: 260px;
  padding: 18px 8px 34px;
  border-bottom: 1px solid #dfe5ec;
  background: repeating-linear-gradient(to bottom, transparent 0, transparent 59px, #edf1f5 60px);
}

.aid-income-v2-line-stage svg {
  width: 100%;
  height: 208px;
  overflow: visible;
}

.aid-income-v2-line-labels {
  display: grid;
  grid-template-columns: repeat(var(--aid-chart-columns), minmax(54px, 1fr));
  gap: 12px;
}

.aid-income-v2-grouped-bars {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;
}

.aid-income-v2-grouped-bar {
  width: min(10px, 20%);
  min-height: 3px;
  border-radius: 5px 5px 2px 2px;
}

.aid-income-v2-pie-layout {
  display: grid;
  grid-template-columns: minmax(190px, 280px) minmax(220px, 1fr);
  align-items: center;
  gap: 34px;
  min-height: 260px;
  padding: 18px 5%;
}

.aid-income-v2-pie {
  width: min(250px, 100%);
  aspect-ratio: 1;
  margin: auto;
  border: 18px solid #fff;
  border-radius: 50%;
  box-shadow: 0 8px 30px rgba(15, 23, 42, .13), inset 0 0 0 1px rgba(15, 23, 42, .05);
}

.aid-income-v2-pie-values { display: grid; gap: 11px; }
.aid-income-v2-pie-value { display: grid; grid-template-columns: 12px 1fr auto; align-items: center; gap: 9px; color: #64748b; font-size: 12px; font-weight: 700; }
.aid-income-v2-pie-value i { width: 10px; height: 10px; border-radius: 3px; }
.aid-income-v2-pie-value strong { color: #172033; font-family: 'Space Mono', monospace; }

.aid-income-v2-chart-column {
  display: grid;
  grid-template-rows: 1fr 34px;
  gap: 8px;
  align-items: end;
  height: 100%;
  min-width: 0;
}

.aid-income-v2-chart-bar-area {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
}

.aid-income-v2-chart-bar {
  display: flex;
  flex-direction: column-reverse;
  width: min(38px, 72%);
  min-height: 3px;
  overflow: hidden;
  border-radius: 8px 8px 3px 3px;
  background: #e7edf2;
  box-shadow: 0 4px 12px rgba(15, 23, 42, .08);
  transition: filter .2s ease, transform .2s ease;
}

.aid-income-v2-chart-bar:hover {
  filter: saturate(1.15);
  transform: translateY(-2px);
}

.aid-income-v2-chart-segment.paid { background: #168653; }
.aid-income-v2-chart-segment.pending { background: #e1a23a; }

.aid-income-v2-chart-label {
  overflow: hidden;
  color: #7b8799;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aid-income-v2-chart-empty {
  display: grid;
  min-height: 210px;
  place-items: center;
  color: #8791a3;
  text-align: center;
}

.aid-income-v2-chart-empty svg {
  display: block;
  margin: 0 auto 9px;
  color: #a9b4c2;
  font-size: 26px;
}

.aid-income-v2-sections {
  display: grid;
  gap: 18px;
}

.aid-income-v2-section {
  overflow: hidden;
  border: 1px solid #dfe6ee;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .04);
}

.aid-income-v2-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid #edf0f4;
}

.aid-income-v2-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.aid-income-v2-section-icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 12px;
  background: #eaf5fa;
  color: #007399;
  font-size: 17px;
}

.aid-income-v2-section-title h2 {
  margin: 0 0 2px;
  color: #243044;
  font-size: 16px;
  font-weight: 800;
}

.aid-income-v2-section-title p {
  margin: 0;
  color: #8791a3;
  font-size: 12px;
}

.aid-income-v2-section-total {
  text-align: right;
}

.aid-income-v2-section-total strong {
  display: block;
  color: #172033;
  font-family: 'Space Mono', monospace;
  font-size: 16px;
}

.aid-income-v2-section-total span {
  color: #8791a3;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.aid-income-v2-table-wrap { overflow-x: auto; }

.aid-income-v2-table {
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
}

.aid-income-v2-table th {
  padding: 11px 16px;
  border-bottom: 1px solid #edf0f4;
  background: #f9fafc;
  color: #748096;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .06em;
  text-align: left;
  text-transform: uppercase;
}

.aid-income-v2-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f2f6;
  color: #4a5668;
  font-size: 13px;
  vertical-align: middle;
}

.aid-income-v2-table tr:last-child td { border-bottom: 0; }
.aid-income-v2-table tbody tr:hover { background: #fbfcfe; }

.aid-income-v2-product {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 210px;
}

.aid-income-v2-image,
.aid-income-v2-image-placeholder {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 10px;
  background: #edf2f7;
  object-fit: cover;
}

.aid-income-v2-image-placeholder {
  display: grid;
  place-items: center;
  color: #93a0b2;
}

.aid-income-v2-product strong {
  display: block;
  max-width: 220px;
  overflow: hidden;
  color: #263243;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aid-income-v2-product small,
.aid-income-v2-muted {
  color: #929cad;
  font-size: 11px;
}

.aid-income-v2-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 999px;
  background: #edf3f8;
  color: #486174;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.aid-income-v2-badge.paid {
  background: #e8f7ef;
  color: #16794b;
}

.aid-income-v2-badge.pending {
  background: #fff4df;
  color: #a46100;
}

.aid-income-v2-amount {
  color: #172033;
  font-family: 'Space Mono', monospace;
  font-weight: 700;
  text-align: right;
}

.aid-income-v2-order-link {
  display: inline-grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 1px solid #cfe0e8;
  border-radius: 9px;
  background: #eef8fb;
  color: #007399;
  cursor: pointer;
  padding: 0;
}

.aid-income-v2-order-link:hover { background: #007399; color: #fff; }

.aid-income-v2-empty {
  padding: 42px 20px;
  color: #8490a3;
  text-align: center;
}

.aid-income-v2-empty svg {
  display: block;
  margin: 0 auto 10px;
  color: #a7b2c0;
  font-size: 25px;
}

.aid-income-v2-empty strong {
  display: block;
  margin-bottom: 4px;
  color: #4a5668;
}

.aid-income-v2-state {
  display: grid;
  min-height: 360px;
  place-items: center;
  padding: 36px;
  border: 1px solid #e1e7ef;
  border-radius: 18px;
  background: #fff;
  color: #68758a;
  text-align: center;
}

.aid-income-v2-spinner {
  width: 42px;
  height: 42px;
  margin: 0 auto 16px;
  border: 4px solid #dce9f0;
  border-top-color: #007399;
  border-radius: 50%;
  animation: aid-income-spin .75s linear infinite;
}

@keyframes aid-income-spin { to { transform: rotate(360deg); } }

.aid-income-v2-error-icon {
  margin-bottom: 12px;
  color: #bd3b3b;
  font-size: 32px;
}

.aid-income-v2-retry {
  margin-top: 14px;
  padding: 9px 16px;
  border: 0;
  border-radius: 10px;
  background: #172033;
  color: #fff;
  cursor: pointer;
  font-weight: 700;
}

@media (max-width: 1100px) {
  .aid-income-v2-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .aid-income-v2-category-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .aid-income-v2-filters { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 680px) {
  .aid-income-v2 { padding: 20px 12px 36px; }
  .aid-income-v2-header { flex-direction: column; }
  .aid-income-v2-summary { grid-template-columns: 1fr; }
  .aid-income-v2-category-summary { grid-template-columns: 1fr; }
  .aid-income-v2-filters { grid-template-columns: 1fr; }
  .aid-income-v2-section-head { align-items: flex-start; }
  .aid-income-v2-section-total { min-width: 92px; }
  .aid-income-v2-chart-head { flex-direction: column; }
  .aid-income-v2-chart-actions { width: 100%; align-items: stretch; }
  .aid-income-v2-chart-period { justify-content: space-between; }
  .aid-income-v2-chart-period select { flex: 1; }
  .aid-income-v2-view-toggle-wrap { justify-content: stretch; }
  .aid-income-v2-view-toggle { width: 100%; }
  .aid-income-v2-chart-visual { grid-template-columns: 60px minmax(0, 1fr); }
  .aid-income-v2-pie-layout { grid-template-columns: 1fr; }
}
`;

const INCOME_SECTIONS = [
  {
    key: "mySellerOrders",
    summaryKey: "seller",
    get title() { return elakayAutoText("h_c997dde576b4a3", "My Seller Orders"); },
    get description() { return elakayAutoText("h_0c2b5ba27cc1e5", "Income earned as the product seller"); },
    icon: <FaStore />
  },
  {
    key: "myReferrerSellerOrders",
    summaryKey: "referrerSeller",
    get title() { return elakayAutoText("h_a871de353aa2ec", "My Referrer Seller Orders"); },
    get description() { return elakayAutoText("h_ea44c4fc047292", "Income from sellers referred by you"); },
    icon: <FaUsers />
  },
  {
    key: "myPromotionOrders",
    summaryKey: "promotion",
    get title() { return elakayAutoText("h_e1f8ed2187831f", "My Promotion Orders"); },
    get description() { return elakayAutoText("h_fc71858d49e8df", "Income generated by your promotions"); },
    icon: <FaBullhorn />
  },
  {
    key: "myRoleIncomeOrders",
    summaryKey: "roleIncome",
    get title() { return elakayAutoText("h_30565b278a4de1", "My Income From Role Orders"); },
    get description() { return elakayAutoText("h_cdce3a522cb999", "Developer, administrator, investment and associate income"); },
    icon: <FaHandHoldingUsd />
  }
];

const EMPTY_INCOME_SUMMARY = {
  totalOrders: 0,
  totalAmount: 0,
  totalPaid: 0,
  totalPending: 0
};

const INCOME_CHART_SERIES = [
  { key: "seller", get label() { return elakayAutoText("h_ec3ef051d5a8bf", "Seller"); }, color: "#007399" },
  { key: "referrerSeller", get label() { return elakayAutoText("h_4393d1d3c01327", "Referrer seller"); }, color: "#7c3aed" },
  { key: "promotion", get label() { return elakayAutoText("h_550ae25c2eeeb9", "Promotion"); }, color: "#e1a23a" },
  { key: "roleIncome", get label() { return elakayAutoText("h_ffe13306dd1497", "Role income"); }, color: "#168653" }
];

const incomeChartSeriesKey = (incomeType = "") => {
  if (incomeType === "seller" || incomeType === "dropshipping_seller") return "seller";
  if (incomeType === "referrerSeller") return "referrerSeller";
  if (incomeType === "promotion") return "promotion";
  return "roleIncome";
};

// Display name of a stored income type (orders.products[].paymentIntent beneficiary key); the key
// itself is what the filter sends. Unknown keys keep the previous humanized form.
const INCOME_TYPE_TEXT = {
  seller: ["h_ec3ef051d5a8bf", "Seller"],
  dropshipping_seller: ["h_inc_type_dropshipping_seller", "Dropshipping seller"],
  referrerSeller: ["h_4393d1d3c01327", "Referrer seller"],
  promotion: ["h_550ae25c2eeeb9", "Promotion"],
  FamilyPayment: ["h_inc_type_family", "Developer payment"],
  AdminPayment: ["h_inc_type_admin", "Administrator payment"],
  InvestmentPayment: ["h_inc_type_investment", "Investor payment"],
  AssociatePayment: ["h_inc_type_associate", "Associate payment"],
};
const incomeTypeLabel = (value = "") =>
  INCOME_TYPE_TEXT[value]
    ? elakayAutoText(...INCOME_TYPE_TEXT[value])
    : String(value)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

// "grouped by <period>" caption of the income chart.
const GROUPED_BY = {
  day: ["h_inc_grouped_day", "grouped by day"],
  week: ["h_inc_grouped_week", "grouped by week"],
  month: ["h_inc_grouped_month", "grouped by month"],
  year: ["h_inc_grouped_year", "grouped by year"],
};

const incomeDateLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(elakayIntlLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const summarizeIncomeItems = (items = []) =>
  items.reduce(
    (summary, item) => {
      const amount = safeAmount(item?.amount);
      summary.totalOrders += 1;
      summary.totalAmount += amount;
      if (item?.paymentStatus === true) summary.totalPaid += amount;
      else summary.totalPending += amount;
      return summary;
    },
    { ...EMPTY_INCOME_SUMMARY }
  );

const chartDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const startOfIncomeWeek = (date) => {
  const start = new Date(date);
  const daysSinceMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - daysSinceMonday);
  start.setHours(0, 0, 0, 0);
  return start;
};

const resolveCustomChartGrouping = (datedItems, dateFrom, dateTo) => {
  const fallbackStart = datedItems[0]?.date;
  const fallbackEnd = datedItems[datedItems.length - 1]?.date;
  const customStart = dateFrom ? new Date(`${dateFrom}T00:00:00`) : fallbackStart;
  const customEnd = dateTo ? new Date(`${dateTo}T23:59:59.999`) : fallbackEnd;
  const rangeInDays = customStart && customEnd
    ? Math.ceil((customEnd - customStart) / (24 * 60 * 60 * 1000))
    : 0;

  if (rangeInDays <= 31) return "day";
  if (rangeInDays <= 120) return "week";
  if (rangeInDays <= 730) return "month";
  return "year";
};

const createIncomeChartBuckets = (
  items = [],
  period = "recent",
  dateFrom = "",
  dateTo = ""
) => {
  const datedItems = items
    .map((item) => ({ item, date: new Date(item?.orderCreatedAt) }))
    .filter(({ date }) => !Number.isNaN(date.getTime()))
    .sort((first, second) => first.date - second.date);

  if (datedItems.length === 0) return [];

  const firstDate = datedItems[0].date;
  const lastDate = datedItems[datedItems.length - 1].date;
  const rangeInDays = Math.ceil((lastDate - firstDate) / (24 * 60 * 60 * 1000));
  const grouping = period === "custom"
    ? resolveCustomChartGrouping(datedItems, dateFrom, dateTo)
    : period === "recent"
      ? (rangeInDays > 45 ? "month" : "day")
      : period;
  const buckets = new Map();

  datedItems.forEach(({ item, date }) => {
    const weekStart = grouping === "week" ? startOfIncomeWeek(date) : null;
    const key = grouping === "year"
      ? String(date.getFullYear())
      : grouping === "month"
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
        : grouping === "week"
          ? `week-${chartDateKey(weekStart)}`
          : chartDateKey(date);
    const label = grouping === "year"
      ? String(date.getFullYear())
      : grouping === "month"
        ? date.toLocaleDateString(elakayIntlLocale(), { month: "short", year: "2-digit" })
        : grouping === "week"
          ? elakayAutoText("h_inc_week_of", "Week of {value0}", { value0: weekStart.toLocaleDateString(elakayIntlLocale(), { month: "short", day: "numeric" }) })
          : date.toLocaleDateString(elakayIntlLocale(), { month: "short", day: "numeric" });
    const bucket = buckets.get(key) || {
      key,
      label,
      total: 0,
      paid: 0,
      pending: 0,
      orders: 0,
      series: {
        seller: 0,
        referrerSeller: 0,
        promotion: 0,
        roleIncome: 0
      }
    };
    const amount = safeAmount(item?.amount);

    bucket.total += amount;
    bucket.series[incomeChartSeriesKey(item?.incomeType)] += amount;
    bucket.orders += 1;
    if (item?.paymentStatus === true) bucket.paid += amount;
    else bucket.pending += amount;
    buckets.set(key, bucket);
  });

  return [...buckets.values()].slice(-12);
};

const customChartDateLabel = (dateFrom, dateTo) => {
  const formatInputDate = (value) => {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString(elakayIntlLocale(), {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
  };
  const start = formatInputDate(dateFrom);
  const end = formatInputDate(dateTo);

  if (start && end) return elakayAutoText("h_inc_custom_range", "Custom date: {value0} – {value1}", { value0: start, value1: end });
  if (start) return elakayAutoText("h_inc_custom_from", "Custom date from {value0}", { value0: start });
  if (end) return elakayAutoText("h_inc_custom_until", "Custom date until {value0}", { value0: end });
  return elakayAutoText("h_a39a910f9e2202", "Custom date");
};

const formatChartMoney = (value) => {
  const amount = safeAmount(value);
  return new Intl.NumberFormat(elakayIntlLocale(), {
    style: "currency",
    currency: "USD",
    notation: Math.abs(amount) >= 1000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(amount) >= 100 ? 0 : 2
  }).format(amount);
};

const IncomeChart = ({
  items = [],
  incomeType = "all",
  chartType = "line",
  period = "recent",
  dateFrom = "",
  dateTo = "",
  onPeriodChange,
  onChartTypeChange
}) => {
  const hasCustomDate = Boolean(dateFrom || dateTo);
  const effectivePeriod = hasCustomDate ? "custom" : period;
  const buckets = useMemo(
    () => createIncomeChartBuckets(
      items,
      effectivePeriod,
      dateFrom,
      dateTo
    ),
    [items, effectivePeriod, dateFrom, dateTo]
  );
  const maximum = Math.max(...buckets.map((bucket) => bucket.total), 0);
  const moneyTicks = [1, 0.75, 0.5, 0.25, 0].map(
    (ratio) => maximum * ratio
  );
  const selectionLabel = incomeType === "all"
    ? elakayAutoText("h_bcef0efaf1b3b8", "All income types")
    : incomeTypeLabel(incomeType);
  const periodLabel = effectivePeriod === "recent"
    ? elakayAutoText("h_inc_recent_periods", "up to 12 recent periods")
    : effectivePeriod === "custom"
      ? customChartDateLabel(dateFrom, dateTo)
      : GROUPED_BY[effectivePeriod] ? elakayAutoText(...GROUPED_BY[effectivePeriod]) : effectivePeriod;
  const selectedSeriesKey = incomeChartSeriesKey(incomeType);
  const visibleSeries = incomeType === "all"
    ? INCOME_CHART_SERIES
    : [{
        key: selectedSeriesKey,
        label: incomeTypeLabel(incomeType),
        color: INCOME_CHART_SERIES.find((series) => series.key === selectedSeriesKey)?.color || "#007399"
      }];
  const linePoints = (series) => buckets.map((bucket, index) => {
    const x = buckets.length === 1 ? 500 : (index / (buckets.length - 1)) * 1000;
    const value = incomeType === "all" ? bucket?.series?.[series.key] : bucket.total;
    const y = maximum > 0 ? 202 - (value / maximum) * 184 : 202;
    return `${x},${y}`;
  }).join(" ");
  const pieSlices = incomeType === "all"
    ? INCOME_CHART_SERIES.map((series) => ({
        ...series,
        value: buckets.reduce((total, bucket) => total + safeAmount(bucket?.series?.[series.key]), 0)
      }))
    : [
        { key: "paid", label: elakayAutoText("h_dc9d4584a55464", "Paid"), color: "#168653", value: buckets.reduce((total, bucket) => total + bucket.paid, 0) },
        { key: "pending", label: elakayAutoText("h_96f608c16cef16", "Pending"), color: "#e1a23a", value: buckets.reduce((total, bucket) => total + bucket.pending, 0) }
      ];
  const pieTotal = pieSlices.reduce((total, slice) => total + slice.value, 0);
  let pieOffset = 0;
  const pieGradient = pieSlices.map((slice) => {
    const start = pieTotal > 0 ? (pieOffset / pieTotal) * 100 : 0;
    pieOffset += slice.value;
    const end = pieTotal > 0 ? (pieOffset / pieTotal) * 100 : 0;
    return `${slice.color} ${start}% ${end}%`;
  }).join(", ");

  const renderLineChart = () => (
    <div className="aid-income-v2-chart-visual">
      <div className="aid-income-v2-chart-y-axis" aria-label={elakayAutoText("h_d35e8fb438aab6", "Income amount scale")}>
        {moneyTicks.map((tick, index) => <span key={`${index}-${tick}`}>{formatChartMoney(tick)}</span>)}
      </div>
      <div className="aid-income-v2-chart-scroll">
        <div className="aid-income-v2-line-stage" style={{ "--aid-chart-columns": buckets.length }}>
          <svg viewBox="0 0 1000 208" preserveAspectRatio="none" role="img" aria-label={elakayAutoText("h_64f2850106ada1", "Income line graph")}>
            {visibleSeries.map((series) => (
              <g key={series.key}>
                <polyline points={linePoints(series)} fill="none" stroke={series.color} strokeWidth="6" vectorEffect="non-scaling-stroke" />
                {buckets.map((bucket, index) => {
                  const x = buckets.length === 1 ? 500 : (index / (buckets.length - 1)) * 1000;
                  const value = incomeType === "all" ? bucket?.series?.[series.key] : bucket.total;
                  const y = maximum > 0 ? 202 - (value / maximum) * 184 : 202;
                  return <circle key={`${series.key}-${bucket.key}`} cx={x} cy={y} r="7" fill={series.color}><title>{`${series.label} · ${bucket.label}: ${formatCurrency(value)}`}</title></circle>;
                })}
              </g>
            ))}
          </svg>
          <div className="aid-income-v2-line-labels" style={{ "--aid-chart-columns": buckets.length }}>
            {buckets.map((bucket) => <div className="aid-income-v2-chart-label" key={bucket.key}>{bucket.label}</div>)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBarChart = (combined = false) => (
    <div className="aid-income-v2-chart-visual">
      <div className="aid-income-v2-chart-y-axis" aria-label={elakayAutoText("h_d35e8fb438aab6", "Income amount scale")}>
        {moneyTicks.map((tick, index) => <span key={`${index}-${tick}`}>{formatChartMoney(tick)}</span>)}
      </div>
      <div className="aid-income-v2-chart-scroll">
        <div className="aid-income-v2-chart-plot" style={{ "--aid-chart-columns": buckets.length }}>
          {buckets.map((bucket) => (
            <div className="aid-income-v2-chart-column" key={bucket.key}>
              <div className="aid-income-v2-chart-bar-area">
                {incomeType === "all" && !combined ? (
                  <div className="aid-income-v2-grouped-bars">
                    {INCOME_CHART_SERIES.map((series) => (
                      <span key={series.key} className="aid-income-v2-grouped-bar" style={{ height: `${maximum > 0 ? Math.max((bucket.series[series.key] / maximum) * 100, 2) : 2}%`, background: series.color }} title={`${series.label}: ${formatCurrency(bucket.series[series.key])}`} />
                    ))}
                  </div>
                ) : (
                  <div className="aid-income-v2-chart-bar" style={{ height: `${maximum > 0 ? Math.max((bucket.total / maximum) * 100, 2) : 2}%` }} title={`${bucket.label}: ${formatCurrency(bucket.total)}`}>
                    <span className="aid-income-v2-chart-segment paid" style={{ height: `${bucket.total > 0 ? (bucket.paid / bucket.total) * 100 : 0}%` }} />
                    <span className="aid-income-v2-chart-segment pending" style={{ height: `${bucket.total > 0 ? (bucket.pending / bucket.total) * 100 : 0}%` }} />
                  </div>
                )}
              </div>
              <div className="aid-income-v2-chart-label">{bucket.label}</div>
            </div>
          ))}
          {combined && (
            <svg className="aid-income-v2-chart-line-overlay" viewBox="0 0 1000 208" preserveAspectRatio="none" aria-label={elakayAutoText("h_8fa4c604e232e1", "Paid income line")}>
              <polyline points={buckets.map((bucket, index) => `${buckets.length === 1 ? 500 : (index / (buckets.length - 1)) * 1000},${maximum > 0 ? 202 - (bucket.paid / maximum) * 184 : 202}`).join(" ")} fill="none" stroke="#007399" strokeWidth="5" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );

  const renderPieChart = () => (
    <div className="aid-income-v2-pie-layout">
      <div className="aid-income-v2-pie" style={{ background: `conic-gradient(${pieGradient || "#edf1f5 0 100%"})` }} role="img" aria-label={elakayAutoText("h_28afeb8f042154", "Income pie chart")} />
      <div className="aid-income-v2-pie-values">
        {pieSlices.map((slice) => (
          <div className="aid-income-v2-pie-value" key={slice.key}>
            <i style={{ background: slice.color }} /><span>{slice.label}</span><strong>{formatCurrency(slice.value)}</strong>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="aid-income-v2-chart" aria-label={elakayAutoText("h_e490d03217741a", "Income graph")}>
      <header className="aid-income-v2-chart-head">
        <div className="aid-income-v2-chart-title">
          <div className="aid-income-v2-chart-title-icon"><FaChartLine /></div>
          <div>
            <h2>{elakayAutoText("h_e490d03217741a", "Income graph")}</h2>
            <p>{selectionLabel} · {periodLabel}</p>
          </div>
        </div>
        <div className="aid-income-v2-chart-actions">
          <div className="aid-income-v2-chart-types" role="group" aria-label={elakayAutoText("h_879575b290bb4f", "Graph type")}>
            <button type="button" className={chartType === "line" ? "active" : ""} aria-pressed={chartType === "line"} onClick={() => onChartTypeChange?.("line")}><FaChartLine />{elakayAutoText("h_ea967600376281", "Line")}</button>
            <button type="button" className={chartType === "pie" ? "active" : ""} aria-pressed={chartType === "pie"} onClick={() => onChartTypeChange?.("pie")}><FaChartPie />{elakayAutoText("h_9318d4f63aeffd", "Pie")}</button>
            <button type="button" className={chartType === "bar" ? "active" : ""} aria-pressed={chartType === "bar"} onClick={() => onChartTypeChange?.("bar")}><FaChartBar />{elakayAutoText("h_e496fd20136d4b", "Bar")}</button>
            <button type="button" className={chartType === "combined" ? "active" : ""} aria-pressed={chartType === "combined"} onClick={() => onChartTypeChange?.("combined")}><FaExchangeAlt />{elakayAutoText("h_b6c533b56588b0", "Combined")}</button>
          </div>
          <div className="aid-income-v2-chart-period">
            <label htmlFor="aid-income-chart-period">{elakayAutoText("h_2c0363103f69e4", "Graph period")}</label>
            <select
              id="aid-income-chart-period"
              value={effectivePeriod}
              disabled={hasCustomDate}
              onChange={(event) => onPeriodChange?.(event.target.value)}
            >
              <option value="recent">{elakayAutoText("h_66ca398e53eb13", "Recent periods")}</option>
              <option value="day">{elakayAutoText("h_987b9ced08d4ac", "Day")}</option>
              <option value="week">{elakayAutoText("h_f82be68a7fb4e7", "Week")}</option>
              <option value="month">{elakayAutoText("h_082bc378cd60e1", "Month")}</option>
              <option value="year">{elakayAutoText("h_879e32326c5217", "Year")}</option>
              <option value="custom" disabled={!hasCustomDate}>{elakayAutoText("h_a39a910f9e2202", "Custom date")}</option>
            </select>
          </div>
          <div className="aid-income-v2-chart-legend" aria-label={elakayAutoText("h_9262b01ece0543", "Graph legend")}>
            {incomeType === "all" ? INCOME_CHART_SERIES.map((series) => (
              <span key={series.key}><i className="aid-income-v2-chart-dot" style={{ background: series.color }} /> {series.label}</span>
            )) : <><span><i className="aid-income-v2-chart-dot paid" />{elakayAutoText("h_dc9d4584a55464", "Paid")}</span><span><i className="aid-income-v2-chart-dot pending" />{elakayAutoText("h_96f608c16cef16", "Pending")}</span></>}
          </div>
        </div>
      </header>

      {buckets.length === 0 ? (
        <div className="aid-income-v2-chart-empty">
          <div>
            <FaChartLine />
            <strong>{elakayAutoText("h_04894236b82eba", "No income to graph for this selection.")}</strong>
          </div>
        </div>
      ) : (
        chartType === "pie" ? renderPieChart() : chartType === "bar" ? renderBarChart() : chartType === "combined" ? renderBarChart(true) : renderLineChart()
      )}
    </section>
  );
};

const IncomeOrdersSection = ({ section, items = [], onViewOrder }) => {
  const sectionSummary = useMemo(() => summarizeIncomeItems(items), [items]);

  return (
    <section
      className="aid-income-v2-section"
      aria-labelledby={`aid-income-section-${section.key}`}
    >
      <header className="aid-income-v2-section-head">
        <div className="aid-income-v2-section-title">
          <div className="aid-income-v2-section-icon">{section.icon}</div>
          <div>
            <h2 id={`aid-income-section-${section.key}`}>{section.title}</h2>
            <p>{section.description}</p>
          </div>
        </div>
      {/* <div className="aid-income-v2-section-total">
          <strong>{formatCurrency(sectionSummary.totalAmount)}</strong>
          <span><span>{sectionSummary.totalOrders}</span>{' '}{elakayAutoText("h_9658403816409e", "orders")}</span>
        </div> */}
        <div className="aid-income-v2-section-total">
  <strong style={{ color: "#dc2626" }}>
    {formatCurrency(sectionSummary.totalAmount)}
  </strong>

  <span>
    <span style={{ color: "#16a34a", fontWeight: 700 }}>
      {sectionSummary.totalOrders}
    </span>{" "}
    {elakayAutoText("h_9658403816409e", "orders")}
  </span>
</div>
      </header>

      {items.length === 0 ? (
        <div className="aid-income-v2-empty">
          <FaReceipt />
          <strong>{elakayAutoText("h_0bcee2bf10f684", "No matching income")}</strong>
          <span>{elakayAutoText("h_4ec7be67c43e88", "Income items in this section will appear here.")}</span>
        </div>
      ) : (
        <div className="aid-income-v2-table-wrap">
          <table className="aid-income-v2-table">
            <thead>
              <tr>
                <th>{elakayAutoText("h_dd3b86d1ef21b9", "Product")}</th>
                <th>{elakayAutoText("h_1d75774c0f96b6", "Order")}</th>
                <th>{elakayAutoText("h_37f4de86a30c82", "Income type")}</th>
                <th>{elakayAutoText("h_22edd6c1ad2ba0", "Order status")}</th>
                <th>{elakayAutoText("h_b41a92bed03261", "Payment")}</th>
                <th>{elakayAutoText("h_eb9a4bc1c0c153", "Date")}</th>
                {section.key === "myReferrerSellerOrders" && <th style={{ textAlign: "right" }}>{elakayAutoText("h_650f0fe358f3b9", "Product price")}</th>}
                {section.key === "myReferrerSellerOrders" && <th style={{ textAlign: "right" }}>{elakayAutoText("h_f55ee032a0dd90", "Product sale")}</th>}
                {section.key === "myReferrerSellerOrders" && <th style={{ textAlign: "right" }}>{elakayAutoText("h_b47c88ec3fa46d", "Seller net revenue")}</th>}
                <th style={{ textAlign: "right" }}>{elakayAutoText("h_1c89b1f2aaab85", "Income")}</th>
                {section.key === "mySellerOrders" && <th style={{ textAlign: "center" }}>{elakayAutoText("h_69bd4ef9fbd089", "View")}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={`${item?.orderId || "order"}-${item?.orderProductId || index}-${item?.incomeType || "income"}`}
                >
                  <td>
                    <div className="aid-income-v2-product">
                      {item?.productImage ? (
                        <img
                          className="aid-income-v2-image"
                          src={getElakayImageLoadUrl(toImageUrl(item.productImage), { width: 120 })}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="aid-income-v2-image-placeholder">
                          <FaShoppingCart />
                        </div>
                      )}
                      <div>
                        <strong>{item?.productName || elakayAutoText("h_dd3b86d1ef21b9", "Product")}</strong>
                        <small>{elakayAutoText("h_1e5ff9e500c20c", "Qty")}{item?.productQty || 0} · {formatCurrency(item?.productPrice)}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span title={item?.orderId || ""}>
                      #{String(item?.orderId || "—").slice(-8)}
                    </span>
                    <div className="aid-income-v2-muted">
                      {item?.sellerShopName || item?.sellerEmail || elakayAutoText("h_ec3ef051d5a8bf", "Seller")}
                    </div>
                  </td>
                  <td>
                    <span className="aid-income-v2-badge">
                      {item?.roleName ? roleDisplayLabel(item.roleName) : incomeTypeLabel(item?.incomeType)}
                    </span>
                  </td>
                  <td>{item?.productStatus ? orderStatusLabel(item.productStatus) : "—"}</td>
                  <td>
                    <span className={`aid-income-v2-badge ${item?.paymentStatus === true ? "paid" : "pending"}`}>
                      {item?.paymentStatus === true ? elakayAutoText("h_dc9d4584a55464", "Paid") : elakayAutoText("h_96f608c16cef16", "Pending")}
                    </span>
                  </td>
                  <td>{incomeDateLabel(item?.orderCreatedAt)}</td>
                  {section.key === "myReferrerSellerOrders" && (
                    <td className="aid-income-v2-amount">
                      <Price
                        showAddToCartButton={false}
                        products={{
                          price: item?.productPrice,
                          salePrice: item?.productSalePrice ?? item?.products?.salePrice
                        }}
                      />
                    </td>
                  )}
                  {section.key === "myReferrerSellerOrders" && (
                    <td className="aid-income-v2-amount">{formatCurrency(item?.amountTotal)}</td>
                  )}
                  {section.key === "myReferrerSellerOrders" && (
                    <td className="aid-income-v2-amount">{formatCurrency(item?.sellerNetRevenue)}</td>
                  )}
                  <td className="aid-income-v2-amount">
                    {formatCurrency(item?.amount)}
                  </td>
                  {section.key === "mySellerOrders" && (
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        className="aid-income-v2-order-link"
                        onClick={() => onViewOrder?.(item)}
                        aria-label={elakayAutoText("h_fd1c8a395ca49f", "View order {value0}", { value0: item?.orderId || "" })}
                        title={elakayAutoText("h_09d5b6ddd3151d", "View order (read only)")}
                      >
                        <FaEye />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const PaymentIntentIncomeDashboard = ({
  infoSource,
  response,
  loading,
  error,
  onRetry
}) => {
  const responseSummary = response?.data?.summary || {};
  const responseData = useMemo(
    () => response?.data?.data || {},
    [response]
  );
  const [incomeType, setIncomeType] = useState("all");
  const [paymentState, setPaymentState] = useState("all");
  const [orderStatus, setOrderStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewMode, setViewMode] = useState("graph");
  const [chartPeriod, setChartPeriod] = useState("recent");
  const [chartType, setChartType] = useState("line");
  const [selectedSellerOrder, setSelectedSellerOrder] = useState(null);

  useEffect(() => {
    setChartType(incomeType === "all" ? "line" : "combined");
  }, [incomeType]);
  const allItems = useMemo(
    () => INCOME_SECTIONS.flatMap((section) =>
      Array.isArray(responseData?.[section.key])
        ? responseData[section.key]
        : []
    ),
    [responseData]
  );
  const incomeTypes = useMemo(
    () => [...new Set(allItems.map((item) => item?.incomeType).filter(Boolean))],
    [allItems]
  );
  const orderStatuses = useMemo(
    () => [...new Set(allItems.map((item) => item?.productStatus).filter(Boolean))],
    [allItems]
  );
  const hasFilters =
    incomeType !== "all" ||
    paymentState !== "all" ||
    orderStatus !== "all" ||
    Boolean(dateFrom) ||
    Boolean(dateTo);
  const matchesFilters = (item) => {
    if (incomeType !== "all" && item?.incomeType !== incomeType) return false;
    if (
      paymentState === "paid" &&
      item?.paymentStatus !== true
    ) return false;
    if (
      paymentState === "pending" &&
      item?.paymentStatus === true
    ) return false;
    if (orderStatus !== "all" && item?.productStatus !== orderStatus) return false;

    const itemDate = new Date(item?.orderCreatedAt);
    if ((dateFrom || dateTo) && Number.isNaN(itemDate.getTime())) return false;
    if (dateFrom) {
      const start = new Date(`${dateFrom}T00:00:00`);
      if (itemDate < start) return false;
    }
    if (dateTo) {
      const end = new Date(`${dateTo}T23:59:59.999`);
      if (itemDate > end) return false;
    }
    return true;
  };
  const filteredData = INCOME_SECTIONS.reduce((result, section) => {
    const items = Array.isArray(responseData?.[section.key])
      ? responseData[section.key]
      : [];
    result[section.key] = items.filter(matchesFilters);
    return result;
  }, {});
  const filteredItems = INCOME_SECTIONS.flatMap(
    (section) => filteredData?.[section.key] || []
  );
  const visibleSummary = hasFilters
    ? summarizeIncomeItems(filteredItems)
    : responseSummary?.global || EMPTY_INCOME_SUMMARY;
  const visibleSections = incomeType === "all"
    ? INCOME_SECTIONS
    : INCOME_SECTIONS.filter((section) => {
        const sectionItems = Array.isArray(responseData?.[section.key])
          ? responseData[section.key]
          : [];
        return sectionItems.some((item) => item?.incomeType === incomeType);
      });

  const clearFilters = () => {
    setIncomeType("all");
    setPaymentState("all");
    setOrderStatus("all");
    setDateFrom("");
    setDateTo("");
    setChartPeriod("recent");
    setChartType("line");
  };

  if (loading) {
    return (
      <div className="aid-income-v2">
        <style>{paymentIntentDashboardStyles}</style>
        <div className="aid-income-v2-inner aid-income-v2-state" role="status">
          <div>
            <div className="aid-income-v2-spinner" />
            <strong>{elakayAutoText("h_f8c25fd2d31d91", "Loading your income dashboard…")}</strong>
            <div>{elakayAutoText("h_4d388979e710b2", "Scanning orders and product payment records.")}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aid-income-v2">
        <style>{paymentIntentDashboardStyles}</style>
        <div className="aid-income-v2-inner aid-income-v2-state" role="alert">
          <div>
            <div className="aid-income-v2-error-icon">!</div>
            <strong>{elakayAutoText("h_db75cc98bf922e", "Income data could not be loaded")}</strong>
            <div>{error}</div>
            {onRetry && (
              <button type="button" className="aid-income-v2-retry" onClick={onRetry}>{elakayAutoText("h_042c862e4467d7", "Try again")}</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aid-income-v2">
      <style>{paymentIntentDashboardStyles}</style>
      <div className="aid-income-v2-inner">
        <header className="aid-income-v2-header">
          <div className="aid-income-v2-title">
            <h1>{elakayAutoText("h_0a499d5ea9c961", "Income dashboard")}</h1>
            <p>{elakayAutoText("h_8e88ff16cabd6f", "Follow seller, referral, promotion and role income recorded on each ordered product.")}</p>
          </div>
          <div className="aid-income-v2-live">{elakayAutoText("h_cb223866d70f17", "PaymentIntent data")}</div>
        </header>

        <div className="aid-income-v2-summary">
          <div className="aid-income-v2-stat total">
            <div className="aid-income-v2-stat-icon"><FaMoneyBillWave /></div>
            <strong>{formatCurrency(visibleSummary?.totalAmount)}</strong>
            <span>{elakayAutoText("h_450a9255fa4b9a", "Total income")}</span>
          </div>
          <div className="aid-income-v2-stat paid">
            <div className="aid-income-v2-stat-icon"><FaChartLine /></div>
            <strong>{formatCurrency(visibleSummary?.totalPaid)}</strong>
            <span>{elakayAutoText("h_3272fda88efd29", "Paid income")}</span>
          </div>
          <div className="aid-income-v2-stat pending">
            <div className="aid-income-v2-stat-icon"><FaCalendarAlt /></div>
            <strong>{formatCurrency(visibleSummary?.totalPending)}</strong>
            <span>{elakayAutoText("h_55c70d9355b448", "Pending income")}</span>
          </div>
          <div className="aid-income-v2-stat orders">
            <div className="aid-income-v2-stat-icon"><FaReceipt /></div>
            <strong>{visibleSummary?.totalOrders || 0}</strong>
            <span>{elakayAutoText("h_b0cfa9c4d9f2ed", "Total orders")}</span>
          </div>
        </div>

        <div className="aid-income-v2-category-summary" aria-label={elakayAutoText("h_66babfc143acf1", "Income categories")}>
          {visibleSections.map((section) => {
            const categorySummary = hasFilters
              ? summarizeIncomeItems(filteredData?.[section.key] || [])
              : responseSummary?.[section.summaryKey] || EMPTY_INCOME_SUMMARY;
            return (
              <div
                className="aid-income-v2-category-card"
                key={section.key}
                style={{ "--aid-income-color": INCOME_CHART_SERIES.find((series) => series.key === section.summaryKey)?.color || "#007399" }}
              >
                <span>{section.title}</span>
                <strong>{formatCurrency(categorySummary?.totalAmount)}</strong>
                <small>{elakayAutoText("h_inc_paid_pending", "{value0} paid · {value1} pending", { value0: formatCurrency(categorySummary?.totalPaid), value1: formatCurrency(categorySummary?.totalPending) })}</small>
              </div>
            );
          })}
        </div>

        <div className="aid-income-v2-filters" aria-label={elakayAutoText("h_eeabeb31926e99", "Income filters")}>
          <div className="aid-income-v2-filter">
            <label htmlFor="aid-income-type">{elakayAutoText("h_37f4de86a30c82", "Income type")}</label>
            <select id="aid-income-type" value={incomeType} onChange={(event) => setIncomeType(event.target.value)}>
              <option value="all">{elakayAutoText("h_bcef0efaf1b3b8", "All income types")}</option>
              {incomeTypes.map((type) => (
                <option key={type} value={type}>{incomeTypeLabel(type)}</option>
              ))}
            </select>
          </div>
          <div className="aid-income-v2-filter">
            <label htmlFor="aid-payment-state">{elakayAutoText("h_b41a92bed03261", "Payment")}</label>
            <select id="aid-payment-state" value={paymentState} onChange={(event) => setPaymentState(event.target.value)}>
              <option value="all">{elakayAutoText("h_a57473336a1200", "Paid and pending")}</option>
              <option value="paid">{elakayAutoText("h_dc9d4584a55464", "Paid")}</option>
              <option value="pending">{elakayAutoText("h_96f608c16cef16", "Pending")}</option>
            </select>
          </div>
          <div className="aid-income-v2-filter">
            <label htmlFor="aid-order-status">{elakayAutoText("h_22edd6c1ad2ba0", "Order status")}</label>
            <select id="aid-order-status" value={orderStatus} onChange={(event) => setOrderStatus(event.target.value)}>
              <option value="all">{elakayAutoText("h_db44f13c18dfa3", "All order statuses")}</option>
              {orderStatuses.map((status) => (
                <option key={status} value={status}>{orderStatusLabel(status)}</option>
              ))}
            </select>
          </div>
          <div className="aid-income-v2-filter">
            <label htmlFor="aid-date-from">{elakayAutoText("h_3f66052a107eaf", "From")}</label>
            <input id="aid-date-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(event) => setDateFrom(event.target.value)} />
          </div>
          <div className="aid-income-v2-filter">
            <label htmlFor="aid-date-to">{elakayAutoText("h_ae79ea1e9c6391", "To")}</label>
            <input id="aid-date-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} />
          </div>
          <button type="button" className="aid-income-v2-clear" onClick={clearFilters} disabled={!hasFilters}>{elakayAutoText("h_719ea396ad92e0", "Clear")}</button>
        </div>

        <div className="aid-income-v2-view-toggle-wrap">
          <div className="aid-income-v2-view-toggle" role="group" aria-label={elakayAutoText("h_e4ced0d40abcfc", "Income display mode")}>
            <button
              type="button"
              className={viewMode === "graph" ? "active" : ""}
              aria-pressed={viewMode === "graph"}
              onClick={() => setViewMode("graph")}
            >
              <FaChartLine />{elakayAutoText("h_9a7405ebcedde5", "Graph")}</button>
            <button
              type="button"
              className={viewMode === "table" ? "active" : ""}
              aria-pressed={viewMode === "table"}
              onClick={() => setViewMode("table")}
            >
              <FaTable />{elakayAutoText("h_0424f6e7026fa4", "Table")}</button>
          </div>
        </div>

        {viewMode === "graph" && (
          <IncomeChart
            items={filteredItems}
            incomeType={incomeType}
            chartType={chartType}
            period={chartPeriod}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onPeriodChange={setChartPeriod}
            onChartTypeChange={setChartType}
          />
        )}

        {viewMode === "table" && (
          <div className="aid-income-v2-sections">
            {visibleSections.map((section) => (
              <IncomeOrdersSection
                key={section.key}
                section={section}
                items={filteredData?.[section.key] || []}
                onViewOrder={setSelectedSellerOrder}
              />
            ))}
          </div>
        )}
      </div>
      {selectedSellerOrder && (
        <OrderShipped
          infoSource={infoSource}
          getOrderID={{
            oid: selectedSellerOrder?.orderId || "",
            pid: selectedSellerOrder?.productId || "",
            opid: selectedSellerOrder?.orderProductId || ""
          }}
          showOrderView={true}
          setShowOrderView={(isOpen) => {
            if (!isOpen) setSelectedSellerOrder(null);
          }}
          readOnly={true}
        />
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssociateIncomeDashboard = ({
  infoSource,
  sellerMenu = [],
  incommeView,
  setIncommeView,
  // Data
  my_Order_as_Seller = [],
  my_Seller_Order = [],
  my_Commision_Orders = {},
  my_Promo_Orders = {},
  paymentIntentIncome = null,
  // Helper functions
  getTotal,
  inComeSeller,
  prodFrTransaction,
  elakayFrais,
  elakayFraisJSON,
  totalFrais,
  calProfit,
  ItemsOrder,
  // Conditions
  Seller,
  Assoociate,
  incommeL,
  onDateRangeChange,
  usePaymentIntentDashboard = false,
  incomeResponse = null,
  incomeLoading = false,
  incomeError = "",
  onIncomeRetry
}) => {
  const [displayMode, setDisplayMode] = useState('chart');

  // Don't render if conditions not met
  if (!(incommeL && (Assoociate?.() || Seller?.()))) return null;

  __setElakayInfoSource(infoSource);

  if (usePaymentIntentDashboard) {
    return (
      <PaymentIntentIncomeDashboard
        infoSource={infoSource}
        response={incomeResponse}
        loading={incomeLoading}
        error={incomeError}
        onRetry={onIncomeRetry}
      />
    );
  }

  const lang = (type, key) => infoSource?.language?.(infoSource, type, key) || key;
  const dashboardText =
    infoSource?.laguangeElakay?.(
      "frontElakayHardcodedText",
      infoSource.languageAPI
    )?.associateincomedashboard || {};

  // Calculate stats
  const totalSales = getTotal?.(my_Order_as_Seller) || 0;
  const totalIncome = resolveIncomeTotal(
    my_Order_as_Seller,
    inComeSeller?.(my_Order_as_Seller) || 0
  );
  const totalFees = totalFrais?.(my_Order_as_Seller) || 0;
  const sellerCommission = resolveIncomeTotal(
    my_Seller_Order,
    elakayFraisJSON?.(my_Seller_Order)?.payAddONSeller || 0
  );
  const storedPaymentIntentTotal = Number(paymentIntentIncome?.totalAmount);
  const displayedIncomeTotal = Number.isFinite(storedPaymentIntentTotal)
    ? storedPaymentIntentTotal
    : totalIncome + sellerCommission;

  const handleExportReport = () => {
    exportRowsToCSV('income-dashboard-report.csv', [
      { Metric: 'Sales', Value: totalSales },
      { Metric: 'Income', Value: displayedIncomeTotal },
      { Metric: 'Fees', Value: totalFees },
      { Metric: 'Profit', Value: my_Commision_Orders?.totalBaseRate || 0 }
    ]);
  };

  // Default menu if not provided
  const menuItems = sellerMenu.length > 0 ? sellerMenu : [
    { key: 'MyOrdersAsSeller', icon: <FaStore /> },
    { key: 'MySellerOrders', icon: <FaUsers /> },
    { key: 'MyOrderIncome', icon: <FaHandHoldingUsd /> },
    { key: 'MyPromoOrders', icon: <FaBullhorn /> }
  ];

  return (
    <>
      <style>{styles}</style>
      <div className="associate-income-dashboard">
        <div className="aid-container">
          {/* Header */}
          <div className="aid-header">
            <div className="aid-header-top">
              <div className="aid-header-title">
               {Seller?.(true) && <h1>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t024}</h1>}
               {Assoociate?.() && <h1>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t025}</h1>}
                <p>{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t026}</p>
              </div>
              <div className="aid-header-actions">
                <button className="aid-action-btn" onClick={() => setDisplayMode('chart')}>
                  <FaCalendarAlt />{__elakayText("t033", "This Month")}</button>
                <button className="aid-action-btn" onClick={handleExportReport}>
                  <FaDownload />{__elakayText("t034", "Export Report")}</button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="aid-stats-grid">
              <StatCard
                type="sales"
                icon={<FaChartLine />}
                value={formatCurrency(totalSales)}
                label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t027}
                trend="up"
                trendValue="+12.5%"
              />
              <StatCard
                type="commission"
                icon={<FaMoneyBillWave />}
                value={formatCurrency(displayedIncomeTotal)}
                label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t028}
                trend="up"
                trendValue="+8.3%"
              />
              <StatCard
                type="fees"
                icon={<FaExchangeAlt />}
                value={formatCurrency(totalFees)}
                label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t029}
                trend="neutral"
                trendValue="0%"
              />
              <StatCard
                type="profit"
                icon={<FaChartPie />}
                value={formatCurrency(my_Commision_Orders?.totalBaseRate || 0)}
                label={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).associateincomedashboard.t030}
                trend="up"
                trendValue="+5.2%"
              />
            </div>
          </div>

          <div className="aid-view-switch-wrapper">
            <div
              className="aid-view-switch"
              role="group"
              aria-label={dashboardText.t066 || elakayAutoText("h_627bc304e73066", "Display mode")}
            >
              <button
                type="button"
                className={displayMode === 'chart' ? 'active' : ''}
                aria-pressed={displayMode === 'chart'}
                onClick={() => setDisplayMode('chart')}
              >
                <FaChartLine />
                {dashboardText.t063 || elakayAutoText("h_9a7405ebcedde5", "Graph")}
              </button>
              <button
                type="button"
                className={displayMode === 'table' ? 'active' : ''}
                aria-pressed={displayMode === 'table'}
                onClick={() => setDisplayMode('table')}
              >
                <FaTable />
                {dashboardText.t065 || elakayAutoText("h_0424f6e7026fa4", "Table")}
              </button>
            </div>
          </div>

          <div hidden={displayMode !== 'chart'}>
            <OrderAnalyticsChart
              infoSource={infoSource}
              my_Order_as_Seller={my_Order_as_Seller}
              my_Seller_Order={my_Seller_Order}
              my_Commision_Orders={my_Commision_Orders}
              my_Promo_Orders={my_Promo_Orders}
              getTotal={getTotal}
              inComeSeller={inComeSeller}
              prodFrTransaction={prodFrTransaction}
              elakayFrais={elakayFrais}
              elakayFraisJSON={elakayFraisJSON}
              totalFrais={totalFrais}
              calProfit={calProfit}
              onDateRangeChange={onDateRangeChange}
            />
          </div>

          {/* Navigation Tabs */}
      {displayMode === 'table' && Assoociate?.() && <nav className="aid-nav-wrapper">
            <ul className="aid-nav-tabs">
              {menuItems.map((item) => (
                <li
                  key={item.key}
                  className={`aid-nav-tab ${item.key === incommeView ? 'active' : ''}`}
                  onClick={() => setIncommeView(item.key)}
                >
                  {item.icon}
                  <span>{lang('title', item.key)}</span>
                </li>
              ))}
            </ul>
          </nav>}

          {/* Content Views */}
          {displayMode === 'table' && incommeView === "MyOrdersAsSeller" && (
            <MyOrdersAsSellerTable
              orders={my_Order_as_Seller}
              infoSource={infoSource}
              getTotal={getTotal}
              inComeSeller={inComeSeller}
              prodFrTransaction={prodFrTransaction}
              elakayFrais={elakayFrais}
              totalFrais={totalFrais}
              calProfit={calProfit}
              ItemsOrder={ItemsOrder}
            />
          )}

          {displayMode === 'table' && incommeView === "MySellerOrders" && Assoociate?.() && (
            <CommissionForMySellersTable
              orders={my_Seller_Order}
              infoSource={infoSource}
              getTotal={getTotal}
              elakayFraisJSON={elakayFraisJSON}
              ItemsOrder={ItemsOrder}
            />
          )}

          {displayMode === 'table' && incommeView === "MyOrderIncome" && Assoociate?.() && (
            <CommissionFromOrdersTable
              commissionData={my_Commision_Orders}
              infoSource={infoSource}
              onShowChart={() => setDisplayMode('chart')}
            />
          )}

          {displayMode === 'table' && incommeView === "MyPromoOrders" && Assoociate?.() && (
            <MyPromotionOrdersTable
              promoData={my_Promo_Orders}
              infoSource={infoSource}
              onShowChart={() => setDisplayMode('chart')}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AssociateIncomeDashboard;
